<script setup>
//#region Imports
import { onBeforeMount, onUpdated, onMounted, reactive, watch, computed, toRef, nextTick } from 'vue';
import { inject } from 'vue';
import axios from 'axios';
import Notification from "@/components/common/SnackBar.vue";
//#endregion

//#region Reactive
const loginState = inject('loginState');
const store = inject('store');

const data = reactive({
    loading: true,
    activeOnly: true,
    selectedType: null,
    promptTypes: [],
    promptsList: [],
    notification: {
      text: "",
      type: "success"
    },
    expanded: [],
    
    // Configure inputs dialog
    showConfigureDialog: false,
    configurePrompt: null,
    parsedInputs: [],
    examples: {},
    configureDialogTab: 0, 
    expandedInputs: [],
    exampleCounter: 0, 
        
    inputSources: [
      { text: 'Dynamic', value: 'dynamic' },
      { text: 'Hierarchy', value: 'hierarchy' },
      { text: 'Template Part', value: 'tmpl_part' },
      { text: 'Level Framework', value: 'level_framework' }
    ],
    hierarchySourceFields: [
      { text: 'Value', value: 'value' },
      { text: 'Descriptor', value: 'descriptor' },
      { text: 'Items - Children', value: 'items' },
      { text: 'Items - Adjacent', value: 'items_adj' },
      { text: 'Sample Doc', value: 'sample' }
    ],
    templatePartSourceFields: [
      { text: 'Value', value: 'value' },
      { text: 'Validation Rules', value: 'validation_rules' }
    ],
    levelFrameworkSourceFields: [
      { text: 'Value', value: 'value' },
      { text: 'Descriptor', value: 'descriptor' },
      { text: 'Full', value: 'full' },
      { text: 'Next', value: 'next' },
      { text: 'Previous', value: 'previous' },
      { text: 'Example Data', value: 'example_data' }
    ],
    templateParts: [], // Store loaded template parts
    loadingTemplateParts: false,
    
    // Save warning dialog
    showSaveWarningDialog: false,
    saveWarningMessage: '',

    // JSON Schema Editor
    showJsonSchemaDialog: false,
    jsonSchemaInput: null,
    jsonOutputText: JSON.stringify([
        {
            "Responsibility": "Responsibility 1",
            "Tasks": ["1234", "2345"]
        },
        {
            "Responsibility": "Responsibility 2",
            "Tasks": ["1234", "2345"]
        }
    ], null, 2),
    jsonOutput: null,
    jsonSchema: null,
    editingJsonSchema: false,
    generatingSchema: false,
    jsonOutputCollapsed: false,
    jsonSchemaCollapsed: false,
    availableWordChecks: [],
    selectedValidationRules: [],
    loadingWordChecks: false,
    schemaPathsForRules: {}, // Map of schema path to array of rule indices
    availableSchemaPaths: [], // List of selectable paths in the schema
    showRuleAssignmentDialog: false,
    selectedSchemaPath: null, // Currently selected schema path for rule assignment
    selectedSchemaNode: null, // Currently selected schema node data
    pathRuleSelections: [], // Temporary selections in the dialog (array of rule indices)
    ruleSearchQuery: '', // Search query for filtering rules
    customRules: {}, // Custom AJV rules by path: { path: { minLength: 5, pattern: "..." } }
    showCustomRuleDialog: false,
    customRuleForm: {
        minLength: null,
        maxLength: null,
        minItems: null,
        maxItems: null,
        pattern: '',
        format: '',
        minimum: null,
        maximum: null,
        enum: []
    },
    expandedRuleGroups: {}, // Track which rule groups are expanded

    // Prompt Type Management
    showPromptTypeDialog: false,
    promptTypeForm: {
        id: null,
        prompt_type: '',
        description: ''
    },
    editingPromptType: false,
    loadingPromptTypes: false,

    // Versioning System
    currentTab: 0, // 0 = list view, 1 = version history
    selectedPromptForVersions: null,
    promptVersions: [],
    loadingVersions: false,
    showVersionCompareDialog: false,
    compareVersionData: {
        version1: null,
        version2: null,
        differences: null
    },
    selectedVersions: [], // For comparison selection
    expandedVersion: null // Currently expanded version row
})
//#endregion

//#region Computed
const filteredPrompts = computed(() => {
    let prompts = data.promptsList;
    if(data.activeOnly){
        prompts = prompts.filter(p => p.active);
    }
    if (data.selectedType){
        prompts = prompts.filter(p => p.prompt_type === data.selectedType);
    }
    return prompts;
});

// Get available hierarchies for selection
const availableHierarchies = computed(() => {
    const hierarchies = store.state.hierarchies?.hierarchies || [];
    return hierarchies.map(h => ({
        text: h.label,
        value: h.ht_id
    }));
});

// Get available template parts for selection
const availableTemplateParts = computed(() => {
    return data.templateParts.map(part => ({
        text: part.tp_name,
        value: part.tp_id
    }));
});

// Get hierarchy values for a specific hierarchy ID
const getHierarchyValues = (htId) => {
    const hierarchies = store.state.hierarchies?.hierarchies || [];
    const hierarchy = hierarchies.find(h => h.ht_id === htId);
    
    if (!hierarchy || !hierarchy.values) {
        return [];
    }
    
    return hierarchy.levels.map(item => ({
        text: item.name,
        value: item.index
    }));
};

// Computed property for select all icon in validation rules multi-select
const selectAllIcon = computed(() => {
    if (data.selectedValidationRules.length === data.availableWordChecks.length) {
        return 'mdi-close-box';
    }
    if (data.selectedValidationRules.length > 0) {
        return 'mdi-minus-box';
    }
    return 'mdi-checkbox-blank-outline';
});
//#endregion

//#region Methods
function load(val){
    axios
    .get("templateBuilder/getPrompts/" + val)
    .then((resp) => {
        data.promptTypes = resp.data.Data.types;
        data.promptsList = resp.data.Data.prompts.map(p => preparePrompt(p));
        data.expanded.splice(0);
    })
    .catch((err) => {
        triggerNotification("Error" + err, "error");
    });
}

function preparePrompt(p){
    p.specificity = p.client_id ? (p.tmpl_id ? "Template Specific" : "Client Default") : "System Default";
    p.editValue = JSON.parse(JSON.stringify(p));
    return p;
}

function watchVal(newVal, oldVal){
    if (newVal !== undefined && value.value !== oldVal) {
        load(newVal);
    }
}

function addNewPrompt(){
    const newPrompt = {
        tmpl_ai_prompt_id: 0,
        client_id: loginState.user?.client_id,
        tmpl_id: value.value,
        system_prompt: "",
        user_prompt: "",
        active: 1,
        editValue: null,
        prompt_type: data.selectedType,
        specificity: "Template Specific",
        configuration_data: null
    };
    newPrompt.editValue = JSON.parse(JSON.stringify(newPrompt));
    data.promptsList.unshift(newPrompt);
    data.expanded.push(0);
}

function resetPrompt(prompt){
    if (prompt.tmpl_ai_prompt_id === 0) {
        const index = data.promptsList.indexOf(prompt);
        if (index > -1) {
            data.promptsList.splice(index, 1);
        }
    }
    prompt.editValue = undefined;
    prompt.editValue = JSON.parse(JSON.stringify(prompt));
    data.expanded.splice(0);
}

function savePrompt(prompt){

    // When using sections, write concatenated text to user_prompt for backward compat
    if (hasUserPromptSections(prompt)) {
        prompt.editValue.user_prompt = getAllUserPromptText(prompt);
    }

    let request = {
        ...prompt.editValue,
        client_id: prompt.editValue.specificity !== "System Default" ? loginState.user?.client_id : null,
        tmpl_id: prompt.editValue.specificity === "Template Specific" ? value.value : null,
        configuration_data: prompt.editValue.configuration_data ? JSON.stringify(prompt.editValue.configuration_data) : null
    }

    axios
        .post("templateBuilder/savePrompt", request)
        .then((resp) => {
            const updated = preparePrompt(resp.data.Data);
            const index = data.promptsList.indexOf(prompt);
            if (index > -1) {
                data.promptsList.splice(index, 1, updated);
            }
            data.expanded.splice(0);
            
            triggerNotification("Saved successfully", "success");
        })
        .catch((err) => {
            triggerNotification("Error" + err, "error");
        });
}

function triggerNotification(msg, type){
  data.notification = {
    text: msg,
    type: type
  };
}

function openConfigureInputsDialog(prompt) {
    data.configurePrompt = prompt;
    data.parsedInputs = parsePromptInputs(getAllUserPromptText(prompt));
    
    loadExistingConfiguration(prompt);
    
    data.configureDialogTab = 0;
    data.expandedInputs = [];
    data.expandedExamples = [];
    
    data.showConfigureDialog = true;
}

function updateInputExampleValue(inputId, exampleKey) {
    const input = data.parsedInputs.find(i => i.id === inputId);
    if (input) {
        input.exampleKey = exampleKey;
    }
}

function parsePromptInputs(userPrompt) {
    if (!userPrompt) return [];
    
    const templateVariableRegex = /\{\{\s*([^}]+)\s*\}\}/g;
    const foundVariables = new Set();
    const inputs = [];

    let match;
    while ((match = templateVariableRegex.exec(userPrompt)) !== null) {
        const key = match[1].trim();
        // Skip control-flow markers: {{#if key}}, {{/if}}, {{else}}, etc.
        if (key.startsWith('#') || key.startsWith('/')) continue;
        if (!foundVariables.has(key)) {
            foundVariables.add(key);
            inputs.push({
                id: Date.now() + Math.random(),
                property: key,
                source: null,
                sourceField: 'value',
                required: true,
                metadata: {},
                hierarchyType: null,
                hierarchyValue: null,
                templatePartId: null,
                exampleKey: null,
                isConfigured: false
            });
        }
    }
    
    return inputs;
}

function loadExistingConfiguration(prompt) {
    let configurationData = null;
    
    try {
        if (prompt.editValue.configuration_data) {
            configurationData = typeof prompt.editValue.configuration_data === 'string' 
                ? JSON.parse(prompt.editValue.configuration_data) 
                : prompt.editValue.configuration_data;
        }
    } catch (error) {
        console.warn('Failed to parse configuration_data:', error);
        return;
    }
    
    if (!configurationData) {
        data.examples = [];
        data.exampleCounter = 0;
        return;
    }
    
    const examplesObj = configurationData.examples || {};
    data.examples = Object.keys(examplesObj).map((key, index) => ({
        id: `loaded_${index}`,
        key: key,
        value: examplesObj[key]
    }));
    data.exampleCounter = data.examples.length;
    
    if (!configurationData.input || !Array.isArray(configurationData.input)) {
        return;
    }
    
    data.parsedInputs.forEach(input => {
        const existingConfig = configurationData.input.find(config => config.property === input.property);
        
        if (existingConfig) {
            input.source = existingConfig.source || null;
            input.sourceField = existingConfig.sourceField || 'value';
            input.required = existingConfig.required !== undefined ? existingConfig.required : true;
            input.isConfigured = !!input.source;
            
            if (existingConfig.metadata) {
                if (input.source === 'hierarchy') {
                    input.hierarchyType = existingConfig.metadata.ht_id || null;
                    input.hierarchyValue = existingConfig.metadata.level || null;
                } else if (input.source === 'tmpl_part') {
                    input.templatePartId = existingConfig.metadata.tp_id || null;
                } else if (input.source === 'example_data') {
                    input.exampleKey = existingConfig.metadata.example_key || null;
                } else if (input.source === 'level_framework' && input.sourceField === 'example_data') {
                    input.levelFrameworkExamplePart = existingConfig.metadata.example_part || null;
                    input.levelFrameworkExampleType = existingConfig.metadata.example_type || null;
                }
            }
        }
    });
    
    const hasTemplatePartInputs = data.parsedInputs.some(input => input.source === 'tmpl_part');
    if (hasTemplatePartInputs && data.templateParts.length === 0) {
        loadTemplateParts();
    }
}

async function loadTemplateParts() {
    if (data.loadingTemplateParts) return;
    
    try {
        data.loadingTemplateParts = true;
        const response = await axios.get("templateBuilder/templateDetails/" + value.value);
        
        if (response.data && response.data.Data && response.data.Data.parts) {
            data.templateParts = response.data.Data.parts;
        } else {
            data.templateParts = [];
        }
    } catch (error) {
        console.error('Error loading template parts:', error);
        data.templateParts = [];
        triggerNotification('Error loading template parts: ' + error.message, 'error');
    } finally {
        data.loadingTemplateParts = false;
    }
}

function updateInputHierarchyType(inputId, htId) {
    const input = data.parsedInputs.find(i => i.id === inputId);
    if (input) {
        input.hierarchyType = htId;
        input.hierarchyValue = null;
    }
}

function updateInputHierarchyValue(inputId, value) {
    const input = data.parsedInputs.find(i => i.id === inputId);
    if (input) {
        input.hierarchyValue = value;
    }
}

function updateInputTemplatePart(inputId, templatePartId) {
    const input = data.parsedInputs.find(i => i.id === inputId);
    if (input) {
        input.templatePartId = templatePartId;
    }
}

function updateInputHierarchySourceField(inputId, sourceField) {
    const input = data.parsedInputs.find(i => i.id === inputId);
    if (input) {
        input.sourceField = sourceField;
    }
}

function onInputSourceChange(input, newSource) {
    input.source = newSource;
    input.isConfigured = !!newSource;

    if (newSource === 'tmpl_part' && data.templateParts.length === 0) {
        loadTemplateParts();
    }

    if (newSource !== 'hierarchy') {
        input.hierarchyType = null;
        input.hierarchyValue = null;
    }
    if (newSource !== 'tmpl_part') {
        input.templatePartId = null;
    }
    if (newSource === 'dynamic') {
        input.sourceField = 'value';
    } else if (newSource === 'hierarchy') {
        input.sourceField = input.sourceField || 'value';
    } else if (newSource === 'tmpl_part') {
        input.sourceField = input.sourceField || 'value';
    } else if (newSource === 'level_framework') {
        input.sourceField = input.sourceField || 'value';
        input.levelFrameworkExamplePart = null;
        input.levelFrameworkExampleType = null;
    } else {
        input.sourceField = null;
    }

    if (newSource !== 'example_data') {
        input.exampleKey = null;
    }

    if (newSource !== 'level_framework') {
        input.levelFrameworkExamplePart = null;
        input.levelFrameworkExampleType = null;
    }
}

function updateInputTemplatePartSourceField(inputId, sourceField) {
    const input = data.parsedInputs.find(i => i.id === inputId);
    if (input) {
        input.sourceField = sourceField;
    }
}

function confirmSaveConfiguration() {
    if (!data.configurePrompt) return;

    const hasTemplateVars = data.parsedInputs.length > 0;

    // Only validate template variables if they exist
    if (hasTemplateVars) {
        const unconfiguredVariables = data.parsedInputs.filter(input => !input.source);
        if (unconfiguredVariables.length > 0) {
            const variableNames = unconfiguredVariables.map(input => input.property).join(', ');
            triggerNotification(`Please configure all template variables. Unconfigured: ${variableNames}`, 'error');
            return;
        }
    }

    // Get existing configuration data to preserve JSON schema if it exists
    let existingConfigData = null;
    try {
        if (data.configurePrompt.editValue.configuration_data) {
            existingConfigData = typeof data.configurePrompt.editValue.configuration_data === 'string'
                ? JSON.parse(data.configurePrompt.editValue.configuration_data)
                : data.configurePrompt.editValue.configuration_data;
        }
    } catch (error) {
        console.warn('Failed to parse existing configuration_data:', error);
    }

    // Build configuration data - include inputs only if template variables exist
    const configurationData = {};
    if (hasTemplateVars) {
        configurationData.input = data.parsedInputs.map((input, index) => {
            const config = {
                id: index + 1,
                property: input.property,
                prompt: "user",
                source: input.source,
                sourceField: input.sourceField || 'value',
                metadata: {},
                required: input.required
            };

            if (input.source === 'hierarchy') {
                config.metadata = {
                    ht_id: input.hierarchyType,
                    level: input.hierarchyValue
                };
            } else if (input.source === 'tmpl_part') {
                config.metadata = {
                    tp_id: input.templatePartId
                };
            } else if (input.source === 'level_framework' && input.sourceField === 'example_data') {
                config.metadata = {
                    example_part: input.levelFrameworkExamplePart,
                    example_type: input.levelFrameworkExampleType
                };
            }

            return config;
        });
    } else if (existingConfigData?.input) {
        // Preserve existing inputs if no template variables are present
        configurationData.input = existingConfigData.input;
    }

    if (existingConfigData?.JSON_Output) {
        configurationData.JSON_Output = existingConfigData.JSON_Output;
    }
    if (existingConfigData?.JSON_Schema) {
        configurationData.JSON_Schema = existingConfigData.JSON_Schema;
    }
    if (existingConfigData?.JSON_Schema_Paths) {
        configurationData.JSON_Schema_Paths = existingConfigData.JSON_Schema_Paths;
    }
    if (existingConfigData?.user_prompt_sections) {
        configurationData.user_prompt_sections = existingConfigData.user_prompt_sections;
    }

    let request;
    const specificity = data.configurePrompt.editValue.specificity;

    // Only create a template copy when System Default AND has non-dynamic inputs
    const requiresTemplateCopy = hasNonDynamicInputs();
    if (requiresTemplateCopy && specificity === 'System Default') {
        request = {
            ...data.configurePrompt.editValue,
            tmpl_ai_prompt_id: 0,
            client_id: loginState.user?.client_id,
            tmpl_id: value.value,
            specificity: 'Template Specific',
            configuration_data: JSON.stringify(configurationData)
        };
    } else {
        request = {
            ...data.configurePrompt.editValue,
            client_id: data.configurePrompt.editValue.specificity !== "System Default" ? loginState.user?.client_id : null,
            tmpl_id: data.configurePrompt.editValue.specificity === "Template Specific" ? value.value : null,
            configuration_data: JSON.stringify(configurationData)
        };
    }

    axios
        .post("templateBuilder/savePrompt", request)
        .then((resp) => {
            const updated = preparePrompt(resp.data.Data);

            if (requiresTemplateCopy && specificity === 'System Default') {
                data.promptsList.unshift(updated);
            } else {
                const index = data.promptsList.indexOf(data.configurePrompt);
                if (index > -1) {
                    data.promptsList.splice(index, 1, updated);
                }
            }

            data.showConfigureDialog = false;
            data.showSaveWarningDialog = false;
            triggerNotification('Configuration saved successfully', 'success');
        })
        .catch((err) => {
            triggerNotification('Error saving configuration: ' + err, 'error');
            data.showSaveWarningDialog = false;
        });
}

function cancelSaveConfiguration() {
    data.showSaveWarningDialog = false;
}

function closeConfigureDialog() {
    data.showConfigureDialog = false;
    data.configurePrompt = null;
    data.parsedInputs = [];
    data.examples = []; 
    data.configureDialogTab = 0; 
    data.expandedInputs = [];
    data.expandedExamples = [];
}

function hasNonDynamicInputs() {
    return data.parsedInputs.length > 0 && data.parsedInputs.some(input => input.source && input.source !== 'dynamic');
}

function saveInputConfiguration() {
    if (!data.configurePrompt) return;

    const specificity = data.configurePrompt.editValue.specificity;
    const requiresTemplateCopy = hasNonDynamicInputs();

    if (requiresTemplateCopy && specificity === 'System Default') {
        data.saveWarningMessage = 'This will create a new Template Specific prompt based on the System Default prompt. The original System Default prompt will remain unchanged.';
    } else if (specificity === 'System Default') {
        data.saveWarningMessage = 'Warning: You are updating a System Default prompt. This change will affect all templates that use this prompt unless overridden.';
    } else if (specificity === 'Client Default') {
        data.saveWarningMessage = 'This will update the existing Client Default prompt with the new configuration.';
    } else {
        data.saveWarningMessage = 'This will update the existing Template Specific prompt with the new configuration.';
    }

    data.showSaveWarningDialog = true;
}

function formatVariableName(property) {
    return `{{ ${property} }}`;
}

function getSourceColor(source) {
    const colors = {
        'dynamic': 'teal',
        'hierarchy': 'blue',
        'tmpl_part': 'green',
        'level_framework': 'orange'
    };
    return colors[source] || 'grey';
}

// --- User Prompt Sections ---
function hasUserPromptSections(prompt) {
    const cd = prompt.editValue?.configuration_data;
    return cd?.user_prompt_sections && cd.user_prompt_sections.length > 0;
}

function getAllUserPromptText(prompt) {
    const cd = prompt.editValue?.configuration_data;
    if (cd?.user_prompt_sections?.length > 0) {
        return [...cd.user_prompt_sections]
            .sort((a, b) => a.order - b.order)
            .map(s => s.text)
            .join('\n');
    }
    return prompt.editValue?.user_prompt || '';
}

function ensureConfigData(prompt) {
    if (!prompt.editValue.configuration_data) {
        prompt.editValue.configuration_data = {};
    }
    return prompt.editValue.configuration_data;
}

function convertToSections(prompt) {
    const text = prompt.editValue.user_prompt || '';
    const cd = ensureConfigData(prompt);
    cd.user_prompt_sections = [{
        id: 's_' + Date.now(),
        title: 'Main',
        text: text,
        cache_control: false,
        order: 0
    }];
    prompt.editValue.user_prompt = '';
    // Trigger reactivity
    prompt.editValue.configuration_data = { ...cd };
}

function revertToSingle(prompt) {
    const cd = prompt.editValue.configuration_data;
    if (!cd?.user_prompt_sections) return;
    const sections = [...cd.user_prompt_sections].sort((a, b) => a.order - b.order);
    prompt.editValue.user_prompt = sections.map(s => s.text).join('\n\n');
    const { user_prompt_sections, ...rest } = cd;
    prompt.editValue.configuration_data = { ...rest };
}

function addSection(prompt) {
    const cd = ensureConfigData(prompt);
    const sections = cd.user_prompt_sections || [];
    sections.push({
        id: 's_' + Date.now(),
        title: '',
        text: '',
        cache_control: false,
        order: sections.length
    });
    prompt.editValue.configuration_data = { ...cd, user_prompt_sections: [...sections] };
}

function removeSection(prompt, sectionId) {
    const cd = prompt.editValue.configuration_data;
    if (!cd?.user_prompt_sections) return;
    const filtered = cd.user_prompt_sections.filter(s => s.id !== sectionId);
    reorderSections(filtered);
    prompt.editValue.configuration_data = { ...cd, user_prompt_sections: filtered };
}

function moveSectionUp(prompt, sectionId) {
    const cd = prompt.editValue.configuration_data;
    if (!cd?.user_prompt_sections) return;
    const sections = [...cd.user_prompt_sections].sort((a, b) => a.order - b.order);
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx <= 0) return;
    const temp = sections[idx].order;
    sections[idx].order = sections[idx - 1].order;
    sections[idx - 1].order = temp;
    prompt.editValue.configuration_data = { ...cd, user_prompt_sections: [...sections] };
}

function moveSectionDown(prompt, sectionId) {
    const cd = prompt.editValue.configuration_data;
    if (!cd?.user_prompt_sections) return;
    const sections = [...cd.user_prompt_sections].sort((a, b) => a.order - b.order);
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx < 0 || idx >= sections.length - 1) return;
    const temp = sections[idx].order;
    sections[idx].order = sections[idx + 1].order;
    sections[idx + 1].order = temp;
    prompt.editValue.configuration_data = { ...cd, user_prompt_sections: [...sections] };
}

function reorderSections(sections) {
    if (!sections) return;
    sections.sort((a, b) => a.order - b.order);
    sections.forEach((s, i) => s.order = i);
}

function getSortedSections(prompt) {
    const cd = prompt.editValue?.configuration_data;
    if (!cd?.user_prompt_sections) return [];
    return [...cd.user_prompt_sections].sort((a, b) => a.order - b.order);
}

// Check if JSON configuration exists
function hasJsonConfiguration() {
    if (!data.configurePrompt?.editValue?.configuration_data) return false;

    try {
        const configData = typeof data.configurePrompt.editValue.configuration_data === 'string'
            ? JSON.parse(data.configurePrompt.editValue.configuration_data)
            : data.configurePrompt.editValue.configuration_data;

        return !!configData?.JSON_Output;
    } catch (error) {
        return false;
    }
}

// Check if JSON schema exists
function hasJsonSchema() {
    if (!data.configurePrompt?.editValue?.configuration_data) return false;

    try {
        const configData = typeof data.configurePrompt.editValue.configuration_data === 'string'
            ? JSON.parse(data.configurePrompt.editValue.configuration_data)
            : data.configurePrompt.editValue.configuration_data;

        return !!configData?.JSON_Schema;
    } catch (error) {
        return false;
    }
}

// Helper function to get color for schema type
function getTypeColor(type) {
    const colorMap = {
        'string': 'mdi-format-text',
        'array': 'mdi-code-brackets',
        'object': 'mdi-code-braces',
        'number': 'mdi-numeric',
        'integer': 'mdi-numeric',
        'boolean': 'mdi-checkbox-marked'
    };
    return colorMap[type] || 'mdi-help-circle';
}

// Render schema with action buttons and collapsible sections
function renderSchemaWithActions(schema, currentPath = '', indent = 0) {
    if (!schema || typeof schema !== 'object') return '';

    const indentStr = '  '.repeat(indent);
    let html = '';

    // Helper to create action button
    const createActionButton = (path, node) => {
        const rulesCount = data.schemaPathsForRules[path]?.length || 0;
        const hasCustomRules = data.customRules[path] && Object.keys(data.customRules[path]).length > 0;
        const hasAnyRules = rulesCount > 0 || hasCustomRules;
        const buttonClass = hasAnyRules ? 'schema-action-btn has-rules' : 'schema-action-btn';
        const buttonId = `schema-btn-${path.replace(/\//g, '-').replace(/\[|\]/g, '')}`;

        let badgeContent = '';
        if (rulesCount > 0 && hasCustomRules) {
            badgeContent = `<span class="rules-badge">${rulesCount}</span>`;
        } else if (rulesCount > 0) {
            badgeContent = `<span class="rules-badge">${rulesCount}</span>`;
        } else if (hasCustomRules) {
            badgeContent = `<span class="rules-badge">${Object.keys(data.customRules[path]).length}</span>`;
        }

        return `<button class="${buttonClass}" data-path="${path}" data-type="${node.type}" id="${buttonId}"><i class="v-icon mdi mdi-cog" style="font-size: 14px;"></i>${badgeContent}</button>`;
    };

    // Helper to create collapse toggle
    const createCollapseToggle = (id) => {
        return `<span class="collapse-toggle" data-target="${id}"><i class="v-icon mdi mdi-chevron-down"></i></span>`;
    };

    if (schema.type === 'object' && schema.properties) {
        const collapseId = `collapse-${currentPath.replace(/\//g, '-')}`;
        html += `${indentStr}${createCollapseToggle(collapseId)}<span class="json-key">{\n</span>`;
        html += `<div class="collapsible-content" id="${collapseId}">`;
        const keys = Object.keys(schema.properties);
        keys.forEach((key, index) => {
            const propPath = `${currentPath}/properties/${key}`;
            const prop = schema.properties[key];
            html += `${indentStr}  <span class="json-key">"${key}"</span>: ${createActionButton(propPath, prop)}`;

            if (prop.type === 'object' || prop.type === 'array') {
                html += '\n';
                html += renderSchemaWithActions(prop, propPath, indent + 1);
            } else {
                html += ` <span class="json-type">${JSON.stringify(prop, null, 2).split('\n').join('\n' + '  '.repeat(indent + 1))}</span>`;
            }

            html += index < keys.length - 1 ? ',\n' : '\n';
        });
        html += `</div>`;
        html += `${indentStr}<span class="json-key">}</span>`;
    } else if (schema.type === 'array' && schema.items) {
        const itemsPath = `${currentPath}/items`;
        const collapseId = `collapse-${currentPath.replace(/\//g, '-')}-array`;
        // Add action button for the array itself (for root or nested arrays)
        html += `${indentStr}${createActionButton(currentPath, schema)}`;
        html += `${createCollapseToggle(collapseId)}<span class="json-key">{\n</span>`;
        html += `<div class="collapsible-content" id="${collapseId}">`;

        // Render all array properties (type, items, maxItems, minItems, etc.)
        const arrayProps = Object.keys(schema);
        arrayProps.forEach((key, propIndex) => {
            if (key === 'type') {
                html += `${indentStr}  <span class="json-key">"type"</span>: <span class="json-value">"array"</span>`;
            } else if (key === 'items') {
                html += `${indentStr}  <span class="json-key">"items"</span>: ${createActionButton(itemsPath, schema.items)}`;
                if (schema.items.type === 'object' || schema.items.type === 'array') {
                    html += '\n';
                    html += renderSchemaWithActions(schema.items, itemsPath, indent + 1);
                } else {
                    html += ` <span class="json-type">${JSON.stringify(schema.items, null, 2).split('\n').join('\n' + '  '.repeat(indent + 1))}</span>`;
                }
            } else {
                // Render other properties like maxItems, minItems, etc.
                html += `${indentStr}  <span class="json-key">"${key}"</span>: <span class="json-value">${JSON.stringify(schema[key])}</span>`;
            }

            if (propIndex < arrayProps.length - 1) {
                html += ',\n';
            } else {
                html += '\n';
            }
        });

        html += `</div>`;
        html += `${indentStr}<span class="json-key">}</span>`;
    } else {
        html += `${indentStr}<span class="json-type">${JSON.stringify(schema, null, 2).split('\n').join('\n' + indentStr)}</span>`;
    }

    return html;
}

// Open rule assignment dialog for a specific path
function openRuleAssignmentDialog(path, node) {
    data.selectedSchemaPath = path;
    data.selectedSchemaNode = node;

    // Initialize temporary selections with existing rules for this path
    data.pathRuleSelections = data.schemaPathsForRules[path] || [];
    data.ruleSearchQuery = '';

    data.showRuleAssignmentDialog = true;
}

// Close rule assignment dialog
function closeRuleAssignmentDialog() {
    data.showRuleAssignmentDialog = false;
    data.selectedSchemaPath = null;
    data.selectedSchemaNode = null;
    data.pathRuleSelections = [];
    data.ruleSearchQuery = '';
}

// Toggle rule selection for current path
function toggleRuleForPath(ruleIndex) {
    const index = data.pathRuleSelections.indexOf(ruleIndex);
    if (index > -1) {
        data.pathRuleSelections.splice(index, 1);
    } else {
        data.pathRuleSelections.push(ruleIndex);
    }
}

// Check if rule is selected for current path
function isRuleSelectedForPath(ruleIndex) {
    return data.pathRuleSelections.includes(ruleIndex);
}

// Group rules by type
function getGroupedRules() {
    const filtered = getFilteredRules();
    const groups = {
        'Word Count': [],
        'Sentence Length': [],
        'Excluded Words': [],
        'Required Words': [],
        'Format': [],
        'Other': []
    };

    filtered.forEach(rule => {
        const check = rule.check;
        if (check.check_function === 'word_count') {
            groups['Word Count'].push(rule);
        } else if (check.check_function === 'sentence_length') {
            groups['Sentence Length'].push(rule);
        } else if (check.check_function === 'word_list' && check.avoid === 1) {
            groups['Excluded Words'].push(rule);
        } else if (check.check_function === 'word_list' && check.include === 1) {
            groups['Required Words'].push(rule);
        } else if (check.check_function === 'infCheck' || check.check_type === 'infCheck' || check.first_word_check === 1) {
            groups['Format'].push(rule);
        } else {
            groups['Other'].push(rule);
        }
    });

    // Remove empty groups
    return Object.entries(groups).filter(([, rules]) => rules.length > 0);
}

// Get filtered rules based on search
function getFilteredRules() {
    if (!data.ruleSearchQuery) {
        return data.availableWordChecks;
    }

    const query = data.ruleSearchQuery.toLowerCase();
    return data.availableWordChecks.filter(rule =>
        rule.text.toLowerCase().includes(query)
    );
}

// Open custom rule dialog
function openCustomRuleDialog() {
    // Load existing custom rules for this path
    if (data.customRules[data.selectedSchemaPath]) {
        Object.assign(data.customRuleForm, data.customRules[data.selectedSchemaPath]);
    } else {
        // Reset form
        data.customRuleForm = {
            minLength: null,
            maxLength: null,
            minItems: null,
            maxItems: null,
            pattern: '',
            format: '',
            minimum: null,
            maximum: null,
            enum: []
        };
    }
    data.showCustomRuleDialog = true;
}

// Save custom rules
function saveCustomRules() {
    const rules = {};

    // Only add non-empty rules
    if (data.customRuleForm.minLength !== null && data.customRuleForm.minLength !== '') {
        rules.minLength = parseInt(data.customRuleForm.minLength);
    }
    if (data.customRuleForm.maxLength !== null && data.customRuleForm.maxLength !== '') {
        rules.maxLength = parseInt(data.customRuleForm.maxLength);
    }
    if (data.customRuleForm.minItems !== null && data.customRuleForm.minItems !== '') {
        rules.minItems = parseInt(data.customRuleForm.minItems);
    }
    if (data.customRuleForm.maxItems !== null && data.customRuleForm.maxItems !== '') {
        rules.maxItems = parseInt(data.customRuleForm.maxItems);
    }
    if (data.customRuleForm.pattern) {
        rules.pattern = data.customRuleForm.pattern;
    }
    if (data.customRuleForm.format) {
        rules.format = data.customRuleForm.format;
    }
    if (data.customRuleForm.minimum !== null && data.customRuleForm.minimum !== '') {
        rules.minimum = parseFloat(data.customRuleForm.minimum);
    }
    if (data.customRuleForm.maximum !== null && data.customRuleForm.maximum !== '') {
        rules.maximum = parseFloat(data.customRuleForm.maximum);
    }
    if (data.customRuleForm.enum && data.customRuleForm.enum.length > 0) {
        rules.enum = data.customRuleForm.enum;
    }

    if (Object.keys(rules).length > 0) {
        data.customRules[data.selectedSchemaPath] = rules;
    } else {
        delete data.customRules[data.selectedSchemaPath];
    }

    data.showCustomRuleDialog = false;
    triggerNotification('Custom rules saved', 'success');

    // Regenerate schema to apply custom rules
    regenerateSchemaWithAssignedRules();
}

// Clear custom rules for current path
function clearCustomRulesForPath() {
    if (data.customRules[data.selectedSchemaPath]) {
        delete data.customRules[data.selectedSchemaPath];
    }

    // Reset form
    data.customRuleForm = {
        minLength: null,
        maxLength: null,
        minItems: null,
        maxItems: null,
        pattern: '',
        format: '',
        minimum: null,
        maximum: null,
        enum: []
    };

    data.showCustomRuleDialog = false;
    triggerNotification('Custom rules cleared', 'success');

    // Regenerate schema to remove custom rules
    regenerateSchemaWithAssignedRules();
}

// Get filtered rules count
function getFilteredRulesCount() {
    return getFilteredRules().length;
}

// Get selected rules for current path
function getSelectedRulesForPath() {
    return data.pathRuleSelections;
}

// Check if all filtered rules are selected
function areAllRulesSelected() {
    const filtered = getFilteredRules();
    return filtered.length > 0 && filtered.every(rule => data.pathRuleSelections.includes(rule.value));
}

// Toggle all filtered rules
function toggleAllRulesForPath() {
    const filtered = getFilteredRules();
    if (areAllRulesSelected()) {
        // Deselect all filtered rules
        filtered.forEach(rule => {
            const index = data.pathRuleSelections.indexOf(rule.value);
            if (index > -1) {
                data.pathRuleSelections.splice(index, 1);
            }
        });
    } else {
        // Select all filtered rules
        filtered.forEach(rule => {
            if (!data.pathRuleSelections.includes(rule.value)) {
                data.pathRuleSelections.push(rule.value);
            }
        });
    }
}

// Check if all rules in a group are selected
function areAllGroupRulesSelected(groupRules) {
    return groupRules.length > 0 && groupRules.every(rule => data.pathRuleSelections.includes(rule.value));
}

// Check if some (but not all) rules in a group are selected
function areSomeGroupRulesSelected(groupRules) {
    const selectedCount = groupRules.filter(rule => data.pathRuleSelections.includes(rule.value)).length;
    return selectedCount > 0 && selectedCount < groupRules.length;
}

// Toggle all rules in a group
function toggleGroupRules(groupRules) {
    if (areAllGroupRulesSelected(groupRules)) {
        // Deselect all rules in this group
        groupRules.forEach(rule => {
            const index = data.pathRuleSelections.indexOf(rule.value);
            if (index > -1) {
                data.pathRuleSelections.splice(index, 1);
            }
        });
    } else {
        // Select all rules in this group
        groupRules.forEach(rule => {
            if (!data.pathRuleSelections.includes(rule.value)) {
                data.pathRuleSelections.push(rule.value);
            }
        });
    }
}

// Save rule assignment
function saveRuleAssignment() {
    if (data.pathRuleSelections.length > 0) {
        data.schemaPathsForRules[data.selectedSchemaPath] = [...data.pathRuleSelections];
    } else {
        delete data.schemaPathsForRules[data.selectedSchemaPath];
    }

    // Regenerate schema with new rules
    regenerateSchemaWithAssignedRules();

    closeRuleAssignmentDialog();
    triggerNotification(`Applied ${data.pathRuleSelections.length} rule(s) to ${data.selectedSchemaPath}`, 'success');
}

// Regenerate schema with assigned rules
function regenerateSchemaWithAssignedRules() {
    if (!data.jsonOutput) return;

    // Infer schema structure
    const inferredSchema = inferSchemaFromJson(data.jsonOutput);

    // Apply rules for each path
    const result = {
        "$schema": "http://json-schema.org/draft-07/schema#",
        ...JSON.parse(JSON.stringify(inferredSchema))
    };

    // Apply template-based validation rules
    for (const [path, ruleIndices] of Object.entries(data.schemaPathsForRules)) {
        const selectedChecks = ruleIndices
            .map(idx => data.availableWordChecks.find(wc => wc.value === idx)?.check)
            .filter(check => check);

        if (selectedChecks.length > 0) {
            const constraints = extractValidationConstraints(selectedChecks);
            applyConstraintsToPath(result, path, constraints);
        }
    }

    // Apply custom AJV rules
    for (const [path, customRules] of Object.entries(data.customRules)) {
        applyCustomRulesToPath(result, path, customRules);
    }

    data.jsonSchema = result;
    // validateJsonOutput(); // Disabled - output is just a structure guide, not validated
}

// Open JSON Schema dialog for configuring response format
async function openJsonSchemaDialog() {
    data.jsonSchemaInput = data.parsedInputs.find(
        input => input.source === 'tmpl_part' && input.sourceField === 'validation_rules'
    );

    if (data.jsonSchemaInput) {
        // Load wordChecks for the template part
        await loadWordChecksForSelection();
    }

    // Load existing JSON schema from configuration_data if available
    let configurationData = null;
    try {
        if (data.configurePrompt.editValue.configuration_data) {
            configurationData = typeof data.configurePrompt.editValue.configuration_data === 'string'
                ? JSON.parse(data.configurePrompt.editValue.configuration_data)
                : data.configurePrompt.editValue.configuration_data;
        }
    } catch (error) {
        console.warn('Failed to parse configuration_data:', error);
    }

    // Initialize with existing data or defaults
    if (configurationData?.JSON_Output) {
        data.jsonOutputText = JSON.stringify(configurationData.JSON_Output, null, 2);
        data.jsonOutput = configurationData.JSON_Output;
    } else {
        // Default example structure
        data.jsonOutputText = JSON.stringify([], null, 2);
        data.jsonOutput = null;
    }

    if (configurationData?.JSON_Schema) {
        data.jsonSchema = configurationData.JSON_Schema;

        // Extract available schema paths if schema exists
        data.availableSchemaPaths = extractSchemaPaths(configurationData.JSON_Schema);
    } else {
        data.jsonSchema = null;
        data.availableSchemaPaths = [];
    }

    // Load schema paths mapping if it exists
    if (configurationData?.JSON_Schema_Paths) {
        data.schemaPathsForRules = configurationData.JSON_Schema_Paths;
    } else {
        data.schemaPathsForRules = {};
    }

    // Load custom AJV rules if they exist
    if (configurationData?.JSON_Custom_Rules) {
        data.customRules = configurationData.JSON_Custom_Rules;
    } else {
        data.customRules = {};
    }

    data.editingJsonSchema = false;
    data.showJsonSchemaDialog = true;
}

// Load wordChecks for multi-select
async function loadWordChecksForSelection() {
    if (!data.jsonSchemaInput || !data.jsonSchemaInput.templatePartId) {
        return;
    }

    data.loadingWordChecks = true;

    try {
        const response = await axios.get('document/wordchecks/'+value.value+"/"+data.jsonSchemaInput.templatePartId);
        const wordChecks = response.data || [];

        // Format wordChecks for display in multi-select
        data.availableWordChecks = wordChecks.map((check, index) => {
            let label = '';

            if (check.check_function === 'word_count') {
                label = `Word Count: ${check.avoid ? 'Max' : 'Min'} ${check.check_value} words`;
            } else if (check.check_function === 'sentence_length') {
                label = `Sentence Length: ${check.avoid ? 'Max' : 'Min'} ${check.check_value} words`;
            } else if (check.check_function === 'row_count') {
                label = `Row Count: ${check.avoid ? 'Max' : 'Min'} ${check.check_value} rows`;
            } else if (check.check_function === 'word_list') {
                if (check.avoid === 1) {
                    label = `Avoid Word: "${check.word}"`;
                } else if (check.include === 1 && check.first_word_check === 1) {
                    label = `Must Start With: "${check.word}"`;
                } else {
                    label = `Include Word: "${check.word}"`;
                }
            } else if (check.check_type === 'infCheck' || check.check_function === 'infCheck') {
                label = 'Must Start With Verb';
            } else {
                label = `${check.check_function}: ${check.check_value || check.word || 'N/A'}`;
            }

            return {
                text: label,
                value: index,
                check: check
            };
        });

        // Select all by default
        data.selectedValidationRules = data.availableWordChecks.map(wc => wc.value);

    } catch (error) {
        console.error('Error loading wordChecks:', error);
        triggerNotification('Error loading validation rules: ' + error.message, 'error');
        data.availableWordChecks = [];
        data.selectedValidationRules = [];
    } finally {
        data.loadingWordChecks = false;
    }
}

// Extract all schema paths for user selection
function extractSchemaPaths(schema, currentPath = '', paths = []) {
    if (!schema || typeof schema !== 'object') return paths;

    // Add current path with descriptive label
    const pathInfo = {
        path: currentPath || '/',
        label: currentPath || '/ (root)',
        type: schema.type
    };
    paths.push(pathInfo);

    // Handle array items
    if (schema.type === 'array' && schema.items) {
        extractSchemaPaths(schema.items, `${currentPath}/items`, paths);
    }

    // Handle object properties
    if (schema.type === 'object' && schema.properties) {
        for (const key in schema.properties) {
            const newPath = `${currentPath}/properties/${key}`;
            const label = currentPath ? `${currentPath.replace(/\/properties\//g, ' > ').replace(/\/items/g, '[]')} > ${key}` : key;
            paths.push({
                path: newPath,
                label: label,
                type: schema.properties[key].type,
                propertyName: key
            });
            extractSchemaPaths(schema.properties[key], newPath, paths);
        }
    }

    return paths;
}

// Generate JSON Schema from user's JSON output and apply validation rules
async function generateJsonSchemaFromValidation() {
    // First, parse and validate the JSON output text
    try {
        data.jsonOutput = JSON.parse(data.jsonOutputText);
    } catch (error) {
        triggerNotification('Invalid JSON format in output. Please fix syntax errors.', 'error');
        return;
    }

    data.generatingSchema = true;

    try {
        // Infer schema structure from the user's JSON output
        const inferredSchema = inferSchemaFromJson(data.jsonOutput);

        // Extract available schema paths for rule targeting
        data.availableSchemaPaths = extractSchemaPaths(inferredSchema);

        // Create base schema
        data.jsonSchema = {
            "$schema": "http://json-schema.org/draft-07/schema#",
            ...JSON.parse(JSON.stringify(inferredSchema))
        };

        // Apply any existing path-specific rules
        if (Object.keys(data.schemaPathsForRules).length > 0) {
            for (const [path, ruleIndices] of Object.entries(data.schemaPathsForRules)) {
                const selectedChecks = ruleIndices
                    .map(idx => data.availableWordChecks.find(wc => wc.value === idx)?.check)
                    .filter(check => check);

                if (selectedChecks.length > 0) {
                    const constraints = extractValidationConstraints(selectedChecks);
                    applyConstraintsToPath(data.jsonSchema, path, constraints);
                }
            }
        }

        // Note: JSON output is just a structure guide, validation is disabled
        // validateJsonOutput();

        triggerNotification('JSON Schema generated successfully. Click ⚙️ buttons to apply validation rules to specific elements.', 'success');
    } catch (error) {
        console.error('Error generating JSON Schema:', error);
        triggerNotification('Error generating JSON Schema: ' + error.message, 'error');
    } finally {
        data.generatingSchema = false;
    }
}

// Infer JSON Schema structure from a JSON value
function inferSchemaFromJson(value) {
    if (value === null) {
        return { type: 'null' };
    }

    if (Array.isArray(value)) {
        const schema = {
            type: 'array'
        };

        // Infer items schema from first element
        if (value.length > 0) {
            schema.items = inferSchemaFromJson(value[0]);
        } else {
            // For empty arrays, default to accepting any type
            schema.items = {};
        }

        return schema;
    }

    const type = typeof value;

    if (type === 'object') {
        const schema = {
            type: 'object',
            properties: {},
            required: []
        };

        // Infer properties from object keys
        for (const key in value) {
            schema.properties[key] = inferSchemaFromJson(value[key]);
            schema.required.push(key);
        }

        return schema;
    }

    if (type === 'string') {
        return { type: 'string' };
    }

    if (type === 'number') {
        return { type: Number.isInteger(value) ? 'integer' : 'number' };
    }

    if (type === 'boolean') {
        return { type: 'boolean' };
    }

    return { type: 'string' }; // fallback
}

// Apply constraints to a specific schema path
function applyConstraintsToPath(schema, targetPath, constraints) {
    if (targetPath === '/' || targetPath === '') {
        // Apply to root
        applyConstraintsRecursive(schema, constraints);
        return;
    }

    // Navigate to the target path
    const pathParts = targetPath.split('/').filter(p => p);
    let current = schema;

    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];

        if (part === 'items') {
            current = current.items;
        } else if (part === 'properties') {
            // Next part should be the property name
            i++;
            const propName = pathParts[i];
            if (current.properties && current.properties[propName]) {
                current = current.properties[propName];
            } else {
                console.warn(`Property ${propName} not found at path`);
                return;
            }
        }

        if (!current) {
            console.warn(`Could not navigate to path: ${targetPath}`);
            return;
        }
    }

    // Apply constraints to this specific node
    applyConstraintsToNode(current, constraints);
}

// Apply custom AJV rules to a specific path
function applyCustomRulesToPath(schema, targetPath, customRules) {
    if (!customRules || Object.keys(customRules).length === 0) return;

    if (targetPath === '/' || targetPath === '') {
        // Apply to root
        Object.assign(schema, customRules);
        return;
    }

    // Navigate to the target path
    const pathParts = targetPath.split('/').filter(p => p);
    let current = schema;

    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];

        if (part === 'items') {
            current = current.items;
        } else if (part === 'properties') {
            // Next part should be the property name
            i++;
            const propName = pathParts[i];
            if (current.properties && current.properties[propName]) {
                current = current.properties[propName];
            } else {
                console.warn(`Property ${propName} not found at path`);
                return;
            }
        }

        if (!current) {
            console.warn(`Could not navigate to path: ${targetPath}`);
            return;
        }
    }

    // Apply custom AJV rules to this specific node
    Object.assign(current, customRules);
}

// Apply constraints to a specific node (non-recursive for targeted application)
function applyConstraintsToNode(node, constraints) {
    if (!node || typeof node !== 'object') return;

    // Apply array-level constraints
    if (node.type === 'array') {
        if (constraints.maxRows !== undefined && constraints.maxRows !== null && constraints.maxRows > 0) {
            if (constraints.explicitRows) {
                node.minItems = constraints.maxRows;
                node.maxItems = constraints.maxRows;
            } else {
                node.maxItems = constraints.maxRows;
            }
        }

        // Also apply to array items if they are strings
        if (node.items && node.items.type === 'string') {
            const stringConstraints = applyStringConstraints(constraints);
            Object.assign(node.items, stringConstraints);
        }
    }

    // Apply string-level constraints
    if (node.type === 'string') {
        const stringConstraints = applyStringConstraints(constraints);
        Object.assign(node, stringConstraints);
    }

    // Apply object-level constraints
    if (node.type === 'object' && node.properties) {
        // Apply to all string properties in this object
        for (const key in node.properties) {
            if (node.properties[key].type === 'string') {
                const stringConstraints = applyStringConstraints(constraints);
                Object.assign(node.properties[key], stringConstraints);
            }
        }
    }
}

// Recursively apply constraints to schema nodes
function applyConstraintsRecursive(node, constraints) {
    if (!node || typeof node !== 'object') return;

    // Apply array-level constraints
    if (node.type === 'array') {
        if (constraints.maxRows !== undefined && constraints.maxRows !== null && constraints.maxRows > 0) {
            if (constraints.explicitRows) {
                node.minItems = constraints.maxRows;
                node.maxItems = constraints.maxRows;
            } else {
                node.maxItems = constraints.maxRows;
            }
        }

        // Recurse into items
        if (node.items) {
            applyConstraintsRecursive(node.items, constraints);
        }
    }

    // Apply string-level constraints
    if (node.type === 'string') {
        const stringConstraints = applyStringConstraints(constraints);
        Object.assign(node, stringConstraints);
    }

    // Apply object-level constraints (recurse into properties)
    if (node.type === 'object' && node.properties) {
        for (const key in node.properties) {
            applyConstraintsRecursive(node.properties[key], constraints);
        }
    }
}

// Extract validation constraints from wordChecks
function extractValidationConstraints(wordChecks) {
    const constraints = {
        avoidWords: [],
        maxWordCount: null,
        maxSentenceLength: null,
        maxRows: null,
        explicitRows: false,
        startsWithGerund: false,
        startsWithVerb: false,
        maxTasks: null
    };

    for (const check of wordChecks) {
        if (check.check_function === 'word_count' && check.avoid === 1) {
            constraints.maxWordCount = check.check_value;
        } else if (check.check_function === 'sentence_length' && check.avoid === 1) {
            constraints.maxSentenceLength = check.check_value;
        } else if (check.check_function === 'row_count' && check.avoid === 1) {
            constraints.maxRows = check.check_value;
        } else if (check.check_function === 'word_list' && check.avoid === 1 && check.word) {
            constraints.avoidWords.push(check.word);
        } else if (check.check_function === 'word_list' && check.include === 1 &&
                   check.first_word_check === 1 && check.word === 'ing') {
            constraints.startsWithGerund = true;
        } else if (check.include === 1 &&
                   (check.check_type === 'infCheck' || check.check_function === 'infCheck')) {
            constraints.startsWithVerb = true;
        }
    }

    return constraints;
}

// Apply string constraints to schema property
function applyStringConstraints(constraints) {
    const props = {};

    if (constraints.maxWordCount && constraints.maxWordCount > 0) {
        props['x-max-words'] = constraints.maxWordCount;
    }

    if (constraints.maxSentenceLength && constraints.maxSentenceLength > 0) {
        props['x-max-sentence-length'] = constraints.maxSentenceLength;
        props['x-validation-scope'] = 'sentence';
    }

    if (constraints.avoidWords && constraints.avoidWords.length > 0) {
        const escapedWords = constraints.avoidWords.map(word =>
            word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );
        const pattern = `^(?!.*\\b(${escapedWords.join('|')})\\b).*$`;
        if (!props.pattern) {
            props.pattern = pattern;
        }
    }

    if (constraints.startsWithGerund) {
        props.pattern = '^\\s*\\w+ing\\b';
    }

    if (constraints.startsWithVerb) {
        props.pattern = '^\\s*[A-Z][a-z]+\\b';
    }

    return props;
}

function saveJsonSchemaConfiguration() {
    try {
        data.jsonOutput = JSON.parse(data.jsonOutputText);
    } catch (error) {
        triggerNotification('Invalid JSON format in output. Please fix syntax errors before saving.', 'error');
        return;
    }

    let configurationData = {};
    try {
        if (data.configurePrompt.editValue.configuration_data) {
            configurationData = typeof data.configurePrompt.editValue.configuration_data === 'string'
                ? JSON.parse(data.configurePrompt.editValue.configuration_data)
                : data.configurePrompt.editValue.configuration_data;
        }
    } catch (error) {
        console.warn('Failed to parse existing configuration_data:', error);
    }

    configurationData.JSON_Output = data.jsonOutput;
    if (data.jsonSchema) {
        configurationData.JSON_Schema = data.jsonSchema;
    }
    if (Object.keys(data.schemaPathsForRules).length > 0) {
        configurationData.JSON_Schema_Paths = data.schemaPathsForRules;
    }
    if (Object.keys(data.customRules).length > 0) {
        configurationData.JSON_Custom_Rules = data.customRules;
    }

    data.configurePrompt.editValue.configuration_data = configurationData;

    data.showJsonSchemaDialog = false;
    triggerNotification('JSON configuration saved successfully', 'success');
}

function closeJsonSchemaDialog() {
    data.showJsonSchemaDialog = false;
    data.jsonSchemaInput = null;
}

function updateJsonOutputText(newValue) {
    data.jsonOutputText = newValue;
    data.jsonOutput = null; // Reset parsed output

    // Clear schema since structure may have changed
    if (data.jsonSchema) {
        data.jsonSchema = null;
    }
}

// Update JSON schema from editor
function updateJsonSchema(newValue) {
    try {
        data.jsonSchema = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
        // validateJsonOutput(); // Disabled - output is just a structure guide, not validated
    } catch (error) {
        triggerNotification('Invalid JSON schema format', 'error');
    }
}

// Prompt Type Management Functions
function openPromptTypeDialog() {
    data.showPromptTypeDialog = true;
}

function closePromptTypeDialog() {
    data.showPromptTypeDialog = false;
    resetPromptTypeForm();
}

function resetPromptTypeForm() {
    data.promptTypeForm = {
        id: null,
        prompt_type: '',
        description: ''
    };
    data.editingPromptType = false;
}

function editPromptType(type) {
    data.promptTypeForm = {
        id: type.id || null,
        prompt_type: type.prompt_type,
        description: type.description
    };
    data.editingPromptType = true;
}

async function savePromptType() {
    if (!data.promptTypeForm.prompt_type || !data.promptTypeForm.description) {
        triggerNotification('Please fill in all fields', 'error');
        return;
    }

    data.loadingPromptTypes = true;

    try {
        const response = await axios.post('templateBuilder/savePromptType', data.promptTypeForm);

        if (response.data && response.data.Data) {
            // Reload prompt types
            await loadPromptTypes();

            triggerNotification(
                data.editingPromptType ? 'Prompt type updated successfully' : 'Prompt type created successfully',
                'success'
            );
            closePromptTypeDialog();
        }
    } catch (error) {
        console.error('Error saving prompt type:', error);
        triggerNotification('Error saving prompt type: ' + (error.response?.data?.Message || error.message), 'error');
    } finally {
        data.loadingPromptTypes = false;
    }
}

async function deletePromptType(type) {
    if (!confirm(`Are you sure you want to delete the prompt type "${type.description}"? This cannot be undone.`)) {
        return;
    }

    data.loadingPromptTypes = true;

    try {
        await axios.post('templateBuilder/deletePromptType', { id: type.id });

        // Reload prompt types
        await loadPromptTypes();

        triggerNotification('Prompt type deleted successfully', 'success');
    } catch (error) {
        console.error('Error deleting prompt type:', error);
        triggerNotification('Error deleting prompt type: ' + (error.response?.data?.Message || error.message), 'error');
    } finally {
        data.loadingPromptTypes = false;
    }
}

async function loadPromptTypes() {
    try {
        const response = await axios.get('templateBuilder/getPromptTypes');
        if (response.data && response.data.Data) {
            data.promptTypes = response.data.Data;
        }
    } catch (error) {
        console.error('Error loading prompt types:', error);
        // If dedicated endpoint doesn't exist, types will be loaded with prompts
        console.warn('Failed to load prompt types from dedicated endpoint, will use types from prompts endpoint');
    }
}

// Versioning System Methods
function switchToListView() {
    data.currentTab = 0;
    data.selectedPromptForVersions = null;
    data.promptVersions = [];
    data.expandedVersion = null;
    data.selectedVersions = [];
}

async function switchToVersionsView(prompt) {
    data.selectedPromptForVersions = prompt;
    await loadPromptVersions(prompt.tmpl_ai_prompt_id);
    data.currentTab = 1; // Switch to versions tab after loading
}

async function loadPromptVersions(promptId) {
    data.loadingVersions = true;
    try {
        const response = await axios.get(`templateBuilder/getPromptVersions/${promptId}`);
        if (response.data && response.data.Data) {
            data.promptVersions = response.data.Data;
        }
    } catch (error) {
        console.error('Error loading prompt versions:', error);
        triggerNotification('Error loading version history: ' + error.message, 'error');
    } finally {
        data.loadingVersions = false;
    }
}

async function compareVersions(version1, version2) {
    if (!data.selectedPromptForVersions) return;

    try {
        const response = await axios.get(
            `templateBuilder/comparePromptVersions/${data.selectedPromptForVersions.tmpl_ai_prompt_id}/${version1}/${version2}`
        );

        if (response.data && response.data.Data) {
            data.compareVersionData = response.data.Data;
            data.showVersionCompareDialog = true;
        }
    } catch (error) {
        console.error('Error comparing versions:', error);
        triggerNotification('Error comparing versions: ' + error.message, 'error');
    }
}

function closeVersionCompareDialog() {
    data.showVersionCompareDialog = false;
    data.compareVersionData = {
        version1: null,
        version2: null,
        differences: null
    };
}

async function setActiveVersion(version) {
    if (!data.selectedPromptForVersions) return;

    if (!confirm(`Are you sure you want to restore version ${version.version}? This will become the active version.`)) {
        return;
    }

    try {
        const response = await axios.post('templateBuilder/setActivePromptVersion', {
            tmpl_ai_prompt_id: data.selectedPromptForVersions.tmpl_ai_prompt_id,
            version: version.version
        });

        if (response.data && response.data.Status === 'OK') {
            triggerNotification(`Successfully restored to version ${version.version}`, 'success');
            // Reload versions to update current status
            await loadPromptVersions(data.selectedPromptForVersions.tmpl_ai_prompt_id);
            // Reload prompts list
            load(value.value);
        }
    } catch (error) {
        console.error('Error setting active version:', error);
        triggerNotification('Error restoring version: ' + (error.response?.data?.Message || error.message), 'error');
    }
}

function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
}

function getChangeTypeColor(changeType) {
    const colors = {
        'CREATE': 'success',
        'UPDATE': 'info',
        'RESTORE': 'warning',
        'ROLLBACK': 'error'
    };
    return colors[changeType] || 'default';
}
//#endregion

//#region Component

const value = defineProps({
    value: { type: Number, required: false }
});

const valueRef = toRef(value, 'value');

watch(valueRef, (newVal, oldVal) => watchVal(newVal, oldVal));

onUpdated(() => {
    // Attach event listeners to schema action buttons after DOM update
    attachSchemaButtonListeners();
})

onMounted(() => {
    // Attach event listeners on mount
    attachSchemaButtonListeners();
})

onBeforeMount(() => {
    load(value.value);
});

// Attach event listeners to dynamically generated schema buttons and collapse toggles
function attachSchemaButtonListeners() {
    nextTick(() => {
        // Attach schema action button listeners
        const buttons = document.querySelectorAll('.schema-action-btn');
        buttons.forEach(button => {
            // Remove existing listener to avoid duplicates
            button.replaceWith(button.cloneNode(true));
        });

        // Re-query after cloning to get fresh buttons
        const freshButtons = document.querySelectorAll('.schema-action-btn');
        freshButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const path = button.getAttribute('data-path');
                const type = button.getAttribute('data-type');
                openRuleAssignmentDialog(path, { type });
            });
        });

        // Attach collapse toggle listeners
        const toggles = document.querySelectorAll('.collapse-toggle');
        toggles.forEach(toggle => {
            toggle.replaceWith(toggle.cloneNode(true));
        });

        const freshToggles = document.querySelectorAll('.collapse-toggle');
        freshToggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const targetId = toggle.getAttribute('data-target');
                const targetElement = document.getElementById(targetId);
                const icon = toggle.querySelector('i');

                if (targetElement) {
                    if (targetElement.style.display === 'none') {
                        targetElement.style.display = '';
                        icon.classList.remove('mdi-chevron-right');
                        icon.classList.add('mdi-chevron-down');
                    } else {
                        targetElement.style.display = 'none';
                        icon.classList.remove('mdi-chevron-down');
                        icon.classList.add('mdi-chevron-right');
                    }
                }
            });
        });
    });
}

//#endregion
</script>

<template>
  <div v-if="value.value">
    <!-- View Mode Tabs -->
    <v-tabs v-model="data.currentTab" class="px-6">
        <v-tab>
            <v-icon left>mdi-format-list-bulleted</v-icon>
            List View
        </v-tab>
        <v-tab :disabled="!data.selectedPromptForVersions">
            <v-icon left>mdi-history</v-icon>
            Version History
            <v-chip v-if="data.selectedPromptForVersions" x-small class="ml-2">
                ID:{{ data.selectedPromptForVersions.tmpl_ai_prompt_id }}
            </v-chip>
        </v-tab>
    </v-tabs>

    <!-- List View -->
    <div v-if="data.currentTab === 0">
        <v-row dense class="px-6 pt-2 d-flex align-center">
            <v-col cols="3">
                <v-select
                    v-model="data.selectedType"
                    :items="data.promptTypes"
                    item-text="description"
                    item-value="prompt_type"
                    label="Filter by Type"
                    clearable
                    dense
                    outlined
                    hide-details
                ></v-select>
            </v-col>
            <v-col cols="2">
                <v-switch v-model="data.activeOnly" label="Show Active Only"></v-switch>
            </v-col>
            <v-col class="d-flex align-center justify-end">
                <v-btn color="secondary" class="mr-2" @click="openPromptTypeDialog" outlined>
                    <v-icon left small>mdi-cog</v-icon>
                    Manage Types
                </v-btn>
                <v-btn color="primary" @click="addNewPrompt" outlined>Add New Prompt</v-btn>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12">
                <v-expansion-panels class="px-6 py-2" multiple v-model="data.expanded">
                <v-expansion-panel
                    v-for="(item,i) in filteredPrompts"
                    :key="i"
                    class="px-4 py-2"
                >
                    <v-expansion-panel-header>
                        <v-row>
                            <v-col class="text-subtitle-1">
                                ID:{{ item.tmpl_ai_prompt_id }} {{ data.promptTypes.find(pt => pt.prompt_type === item.prompt_type)?.description || item.prompt_type }}
                            </v-col>
                            <v-col>
                                <v-chip v-if="!item.tmpl_ai_prompt_id" small color="success" class="mr-2">new</v-chip>
                                <v-chip small
                                    :color="item.specificity === 'Template Specific' ? 'success' : item.specificity === 'Client Default' ? 'info' : ''"
                                    class="mr-2">
                                    {{ item.specificity }}
                                </v-chip>
                            </v-col>
                            <v-col cols="2" class="d-flex justify-end pr-4">
                                <v-icon color="success" v-if="item.active">check_circle_outline</v-icon>
                                <v-icon v-else>not_interested</v-icon>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row dense>
                            <v-col cols="2">
                                <v-switch v-model="item.editValue.active" label="Active"></v-switch>
                            </v-col>
                            <v-col cols="2">
                                <v-select
                                    v-model="item.editValue.prompt_type"
                                    :items="data.promptTypes"
                                    item-text="description"
                                    item-value="prompt_type"
                                    label="Type"
                                    dense
                                    outlined
                                    hide-details
                                ></v-select>
                            </v-col>
                            <v-col cols="2">
                                <v-select
                                    v-model="item.editValue.specificity"
                                    :items="['Template Specific', 'Client Default', 'System Default']"
                                    label=""
                                    dense
                                    outlined
                                    hide-details
                                ></v-select>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field dense v-model="item.editValue.prompt_name" outlined label="Prompt Name"></v-text-field>
                            </v-col>
                            <v-col cols="2" class="d-flex align-center" style="height:100%; padding-top:5px">
                                <v-btn
                                    icon
                                    @click="openConfigureInputsDialog(item)"
                                    title="Configure Prompt Inputs"
                                    class="mr-1"
                                >
                                    <v-icon>mdi-cog</v-icon>
                                </v-btn>
                                <v-btn
                                    v-if="item.tmpl_ai_prompt_id"
                                    icon
                                    @click="switchToVersionsView(item)"
                                    title="View Version History"
                                    color="info"
                                >
                                    <v-icon>mdi-history</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row dense class="pt-2">
                            <v-col cols="6">
                                System Prompt:<br/>
                                <v-textarea autoGrow outlined v-model="item.editValue.system_prompt"></v-textarea>
                            </v-col>
                            <v-col cols="6">
                                <!-- Legacy single User Prompt -->
                                <template v-if="!hasUserPromptSections(item)">
                                    User Prompt:<br/>
                                    <v-textarea autoGrow outlined v-model="item.editValue.user_prompt"></v-textarea>
                                    <v-btn x-small outlined color="grey" @click="convertToSections(item)">
                                        <v-icon x-small left>mdi-arrow-split-vertical</v-icon>
                                        Convert to Sections
                                    </v-btn>
                                </template>

                                <!-- Multi-section User Prompt -->
                                <template v-else>
                                    <div class="d-flex align-center mb-2">
                                        <span>User Prompt Sections:</span>
                                        <v-spacer></v-spacer>
                                        <v-btn x-small outlined color="grey" class="mr-1" @click="revertToSingle(item)">
                                            <v-icon x-small left>mdi-arrow-collapse-all</v-icon>
                                            Revert to Single
                                        </v-btn>
                                        <v-btn x-small outlined color="primary" @click="addSection(item)">
                                            <v-icon x-small left>mdi-plus</v-icon>
                                            Add Section
                                        </v-btn>
                                    </div>
                                    <v-card
                                        v-for="section in getSortedSections(item)"
                                        :key="section.id"
                                        outlined
                                        class="mb-2 pa-2"
                                    >
                                        <div class="d-flex align-center mb-1">
                                            <v-text-field
                                                v-model="section.title"
                                                placeholder="Section title"
                                                dense
                                                outlined
                                                hide-details
                                                class="mr-2"
                                                style="max-width: 200px;"
                                            ></v-text-field>
                                            <v-switch
                                                v-model="section.cache_control"
                                                label="Cache"
                                                dense
                                                hide-details
                                                class="mt-0 mr-2"
                                            ></v-switch>
                                            <v-spacer></v-spacer>
                                            <v-btn icon x-small class="mr-1" @click="moveSectionUp(item, section.id)" :disabled="section.order === 0">
                                                <v-icon x-small>mdi-arrow-up</v-icon>
                                            </v-btn>
                                            <v-btn icon x-small class="mr-1" @click="moveSectionDown(item, section.id)" :disabled="section.order >= getSortedSections(item).length - 1">
                                                <v-icon x-small>mdi-arrow-down</v-icon>
                                            </v-btn>
                                            <v-btn icon x-small color="error" @click="removeSection(item, section.id)">
                                                <v-icon x-small>mdi-delete</v-icon>
                                            </v-btn>
                                        </div>
                                        <v-textarea
                                            v-model="section.text"
                                            auto-grow
                                            outlined
                                            dense
                                            hide-details
                                            rows="3"
                                        ></v-textarea>
                                    </v-card>
                                </template>
                            </v-col>                    
                        </v-row>
                        <v-row>
                            <v-col class="d-flex justify-end">
                                <v-btn color="primary" outlined class="mr-2" @click="resetPrompt(item)">Cancel</v-btn>
                                <v-btn color="primary" @click="savePrompt(item)">Save Prompt</v-btn>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-col>
    </v-row>
    </div>

    <!-- Version History View -->
    <div v-if="data.currentTab === 1" class="px-6 py-4">
        <div v-if="data.selectedPromptForVersions">
            <!-- Header -->
            <div class="d-flex align-center mb-4">
                <v-btn text small @click="switchToListView" class="mr-3 px-1">
                    <v-icon small class="mr-1">mdi-arrow-left</v-icon>
                    Back
                </v-btn>
                <v-divider vertical class="mx-2" style="height: 24px;"></v-divider>
                <span class="text-subtitle-1 font-weight-medium ml-2">
                    {{ data.selectedPromptForVersions.editValue.prompt_name || 'Unnamed Prompt' }}
                </span>
                <v-chip x-small outlined class="ml-3">{{ data.promptVersions.length }} version{{ data.promptVersions.length !== 1 ? 's' : '' }}</v-chip>
                <v-spacer></v-spacer>
                <v-btn
                    v-if="data.selectedVersions.length === 2"
                    small
                    color="primary"
                    outlined
                    @click="compareVersions(data.selectedVersions[0], data.selectedVersions[1])"
                >
                    <v-icon small left>mdi-compare</v-icon>
                    Compare Selected
                </v-btn>
                <span v-else-if="data.selectedVersions.length === 1" class="text-caption grey--text">
                    Select one more version to compare
                </span>
            </div>

            <v-progress-linear v-if="data.loadingVersions" indeterminate color="primary" style="margin: 0;"></v-progress-linear>

            <!-- Version Table -->
            <v-simple-table v-if="!data.loadingVersions && data.promptVersions.length > 0" class="version-table">
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th style="width: 40px;"></th>
                            <th style="width: 80px;">Version</th>
                            <th style="width: 90px;">Status</th>
                            <th style="width: 90px;">Type</th>
                            <th>Summary</th>
                            <th style="width: 160px;">Date</th>
                            <th style="width: 120px;">Author</th>
                            <th style="width: 50px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="version in data.promptVersions"
                            :key="version.version"
                            :class="{ 'version-active-row': version.is_current }"
                            style="cursor: pointer;"
                            @click="data.expandedVersion === version.version ? data.expandedVersion = null : data.expandedVersion = version.version"
                        >
                            <td @click.stop>
                                <v-checkbox
                                    :value="version.version"
                                    v-model="data.selectedVersions"
                                    :disabled="data.selectedVersions.length >= 2 && !data.selectedVersions.includes(version.version)"
                                    hide-details
                                    dense
                                    class="mt-0 pt-0"
                                ></v-checkbox>
                            </td>
                            <td class="font-weight-medium">v{{ version.version }}</td>
                            <td>
                                <v-chip v-if="version.is_current" x-small color="success" dark>Active</v-chip>
                                <span v-else class="grey--text text--darken-1 text-caption">—</span>
                            </td>
                            <td>
                                <v-chip x-small outlined :color="getChangeTypeColor(version.change_type)">
                                    {{ version.change_type || '—' }}
                                </v-chip>
                            </td>
                            <td>
                                <span class="text-body-2">{{ version.change_summary || '—' }}</span>
                                <span v-if="version.changed_fields && version.changed_fields.length > 0" class="ml-2">
                                    <v-chip v-for="field in version.changed_fields" :key="field" x-small class="mr-1" outlined>{{ field }}</v-chip>
                                </span>
                            </td>
                            <td class="text-caption grey--text text--darken-1">{{ formatDate(version.created_at) }}</td>
                            <td class="text-caption">{{ version.created_by_username || version.created_by || '—' }}</td>
                            <td class="text-right">
                                <v-btn
                                    v-if="!version.is_current"
                                    icon
                                    x-small
                                    @click.stop="setActiveVersion(version)"
                                    title="Restore this version"
                                    color="primary"
                                >
                                    <v-icon small>mdi-restore</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                        <!-- Expanded detail row -->
                        <tr v-for="version in data.promptVersions" :key="'detail-' + version.version" v-show="data.expandedVersion === version.version">
                            <td colspan="8" class="pa-0">
                                <div class="pa-4" style="background: #fafafa;">
                                    <v-row dense>
                                        <v-col cols="6">
                                            <div class="text-caption grey--text mb-1">System Prompt</div>
                                            <pre class="version-pre">{{ version.system_prompt || '(empty)' }}</pre>
                                        </v-col>
                                        <v-col cols="6">
                                            <div class="text-caption grey--text mb-1">User Prompt</div>
                                            <pre class="version-pre">{{ version.user_prompt || '(empty)' }}</pre>
                                        </v-col>
                                    </v-row>
                                    <v-row v-if="version.configuration_data" dense class="mt-2">
                                        <v-col cols="12">
                                            <div class="text-caption grey--text mb-1">Configuration</div>
                                            <pre class="version-pre">{{ JSON.stringify(version.configuration_data, null, 2) }}</pre>
                                        </v-col>
                                    </v-row>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>

            <div v-if="!data.loadingVersions && data.promptVersions.length === 0" class="text-center pa-8 grey--text">
                <v-icon size="40" color="grey lighten-1" class="mb-2">mdi-history</v-icon>
                <div class="text-body-2">No version history available for this prompt.</div>
            </div>
        </div>
    </div>

    <!-- Version Compare Dialog -->
    <v-dialog
        v-model="data.showVersionCompareDialog"
        max-width="1400"
    >
        <v-card v-if="data.compareVersionData.version1 && data.compareVersionData.version2">
            <v-card-title class="py-3">
                <span class="text-subtitle-1 font-weight-medium">
                    Comparing v{{ data.compareVersionData.version1.version }} &rarr; v{{ data.compareVersionData.version2.version }}
                </span>
                <v-spacer></v-spacer>
                <v-btn icon small @click="closeVersionCompareDialog">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-4">
                <v-simple-table dense class="compare-table">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th style="width: 160px;">Field</th>
                                <th>v{{ data.compareVersionData.version1.version }}</th>
                                <th>v{{ data.compareVersionData.version2.version }}</th>
                                <th style="width: 80px;">Changed</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(diff, field) in data.compareVersionData.differences" :key="field" :class="{ 'compare-changed-row': diff.changed }">
                                <td class="font-weight-medium text-caption">{{ field }}</td>
                                <td>
                                    <pre v-if="typeof diff.old === 'object'" class="version-pre" style="max-height: 150px;">{{ JSON.stringify(diff.old, null, 2) }}</pre>
                                    <pre v-else class="version-pre" style="max-height: 150px;">{{ diff.old || '(empty)' }}</pre>
                                </td>
                                <td>
                                    <pre v-if="typeof diff.new === 'object'" class="version-pre" style="max-height: 150px;">{{ JSON.stringify(diff.new, null, 2) }}</pre>
                                    <pre v-else class="version-pre" style="max-height: 150px;">{{ diff.new || '(empty)' }}</pre>
                                </td>
                                <td class="text-center">
                                    <v-icon v-if="diff.changed" small color="warning">mdi-delta</v-icon>
                                    <v-icon v-else small color="grey lighten-1">mdi-minus</v-icon>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Configure Prompt Inputs Dialog -->
    <v-dialog
        v-model="data.showConfigureDialog"
        max-width="1200"
        persistent
    >
        <v-card>
            <v-card-title class="headline">
                Configure Prompt Inputs
                <v-spacer></v-spacer>
                <v-btn icon @click="closeConfigureDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <div v-if="data.configurePrompt">
                    <div class="mb-4">
                        <strong>Prompt:</strong> {{ data.configurePrompt.editValue.prompt_name || 'Unnamed Prompt' }}
                    </div>

                    <!-- Tabs for Input Configuration -->
                    <v-tabs v-model="data.configureDialogTab" class="mb-4">
                        <v-tab>
                            <v-icon left>mdi-format-list-bulleted</v-icon>
                            Template Variables
                            <v-chip v-if="data.parsedInputs.length > 0" x-small class="ml-2">{{ data.parsedInputs.length }}</v-chip>
                            <v-chip v-if="data.parsedInputs.filter(input => !input.isConfigured).length > 0"
                                   x-small
                                   color="warning"
                                   text-color="white"
                                   class="ml-1">
                                {{ data.parsedInputs.filter(input => !input.isConfigured).length }} need configuration
                            </v-chip>
                        </v-tab>
                        <v-tab>
                            <v-icon left>mdi-code-json</v-icon>
                            JSON Response Format
                        </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="data.configureDialogTab">
                        <!-- Template Variables Tab -->
                        <v-tab-item>
                            <v-card flat>
                                <v-card-text class="pa-0">
                                    <div v-if="data.parsedInputs.length === 0" class="text-center pa-8 grey--text">
                                        <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-format-list-bulleted</v-icon>
                                        <div class="text-h6 mb-2">No Template Variables Found</div>
                                        <div class="text-body-2">
                                            Template variables should be in the format: <code v-text="'{{ variable_name }}'"></code>
                                        </div>
                                    </div>

                                    <div v-else>
                                        <!-- Warning for unconfigured variables -->
                                        <div v-if="data.parsedInputs.filter(input => !input.isConfigured).length > 0" 
                                             class="mb-4 pa-3 warning lighten-4 rounded">
                                            <div class="d-flex align-center">
                                                <v-icon left color="warning darken-2">mdi-alert</v-icon>
                                                <strong>{{ data.parsedInputs.filter(input => !input.isConfigured).length }} variable{{ data.parsedInputs.filter(input => !input.isConfigured).length > 1 ? 's' : '' }} require{{ data.parsedInputs.filter(input => !input.isConfigured).length > 1 ? '' : 's' }} configuration</strong>
                                            </div>
                                            <div class="text-body-2 mt-1">
                                                Please select an input source for each template variable before saving.
                                            </div>
                                        </div>

                                        <!-- Info banner for configured variables -->
                                        <div v-else class="mb-4 pa-3 blue lighten-5 rounded">
                                            <div class="d-flex align-center">
                                                <v-icon left color="primary">mdi-check-circle</v-icon>
                                                <strong>All {{ data.parsedInputs.length }} template variable{{ data.parsedInputs.length > 1 ? 's are' : ' is' }} configured</strong>
                                            </div>
                                            <div class="text-body-2 mt-1">
                                                All variables have been configured with input sources.
                                            </div>
                                        </div>

                                        <v-expansion-panels 
                                            v-model="data.expandedInputs" 
                                            multiple
                                            accordion
                                        >
                                            <v-expansion-panel
                                                v-for="(input) in data.parsedInputs"
                                                :key="input.id"
                                                :class="{ 'warning-border': !input.isConfigured }"
                                            >
                                                <v-expansion-panel-header>
                                                    <div class="d-flex align-center justify-space-between w-100 pr-4">
                                                        <div class="d-flex align-center">
                                                            <code v-text="formatVariableName(input.property)" class="pa-1 grey lighten-3 rounded mr-3"></code>
                                                            <v-chip v-if="input.source" 
                                                                   x-small 
                                                                   :color="getSourceColor(input.source)" 
                                                                   text-color="white">
                                                                {{ data.inputSources.find(s => s.value === input.source)?.text || input.source }}
                                                            </v-chip>
                                                            <v-chip v-else 
                                                                   x-small 
                                                                   color="warning" 
                                                                   text-color="white">
                                                                <v-icon left x-small>mdi-alert</v-icon>
                                                                Not Configured
                                                            </v-chip>
                                                        </div>
                                                        <div class="d-flex align-center">
                                                            <v-icon v-if="input.required" small color="error" class="mr-2">mdi-asterisk</v-icon>
                                                            <v-icon v-if="!input.isConfigured" small color="warning" class="mr-2">mdi-alert</v-icon>
                                                        </div>
                                                    </div>
                                                </v-expansion-panel-header>
                                                <v-expansion-panel-content>
                                                    <v-row dense class="mt-2">
                                                        <v-col cols="4">
                                                            <v-select
                                                                :value="input.source"
                                                                :items="data.inputSources"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Input Source *"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                :placeholder="input.source ? undefined : 'Select a source...'"
                                                                :color="input.source ? 'primary' : 'warning'"
                                                                @input="onInputSourceChange(input, $event)"
                                                            >
                                                                <template v-slot:prepend-item>
                                                                    <div v-if="!input.source" class="px-4 py-2 warning lighten-4">
                                                                        <div class="d-flex align-center">
                                                                            <v-icon left small color="warning darken-2">mdi-alert</v-icon>
                                                                            <span class="text-body-2">Please select an input source</span>
                                                                        </div>
                                                                    </div>
                                                                    <v-divider v-if="!input.source"></v-divider>
                                                                </template>
                                                            </v-select>
                                                        </v-col>
                                                        <v-col cols="4">
                                                            <div class="d-flex align-center" style="height: 40px;">
                                                                <v-checkbox
                                                                    v-model="input.required"
                                                                    label="Required"
                                                                    hide-details
                                                                    dense
                                                                ></v-checkbox>
                                                            </div>
                                                        </v-col>
                                                    </v-row>

                                                    <!-- Source-specific configuration sections -->
                                                    <!-- Hierarchy Selection -->
                                                    <v-row v-if="input.source === 'hierarchy'" dense class="mt-2">
                                                        <v-col cols="4">
                                                            <v-select
                                                                :value="input.hierarchyType"
                                                                :items="availableHierarchies"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Select Hierarchy Type"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                clearable
                                                                @input="updateInputHierarchyType(input.id, $event)"
                                                            ></v-select>
                                                        </v-col>
                                                        <v-col cols="4">
                                                            <v-select
                                                                :value="input.hierarchyValue"
                                                                :items="getHierarchyValues(input.hierarchyType)"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Select Hierarchy Level"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                clearable
                                                                :disabled="!input.hierarchyType"
                                                                @input="updateInputHierarchyValue(input.id, $event)"
                                                            ></v-select>
                                                        </v-col>
                                                        <v-col cols="4">
                                                            <v-select
                                                                :value="input.sourceField || 'value'"
                                                                :items="data.hierarchySourceFields"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Source Field"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                @input="updateInputHierarchySourceField(input.id, $event)"
                                                            ></v-select>
                                                        </v-col>
                                                    </v-row>

                                                    <!-- Level Framework Selection -->
                                                    <v-row v-if="input.source === 'level_framework'" dense class="mt-2">
                                                        <v-col cols="4">
                                                            <v-select
                                                                :value="input.sourceField || 'value'"
                                                                :items="data.levelFrameworkSourceFields"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Source Field"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                @input="updateInputHierarchySourceField(input.id, $event)"
                                                            ></v-select>
                                                        </v-col>
                                                        <v-col v-if="input.sourceField === 'example_data'" cols="4">
                                                            <v-text-field
                                                                v-model="input.levelFrameworkExamplePart"
                                                                label="Example Part"
                                                                outlined
                                                                dense
                                                                hide-details
                                                                placeholder="Enter part name..."
                                                                hint="Part name for the example value in level framework"
                                                                persistent-hint
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col v-if="input.sourceField === 'example_data'" cols="4">
                                                            <v-text-field
                                                                v-model="input.levelFrameworkExampleType"
                                                                label="Example Type"
                                                                outlined
                                                                dense
                                                                hide-details
                                                                placeholder="Enter type name..."
                                                                hint="Type name for the example value in level framework"
                                                                persistent-hint
                                                            ></v-text-field>
                                                        </v-col>
                                                    </v-row>

                                                    <!-- Template Part Selection -->
                                                    <v-row v-if="input.source === 'tmpl_part'" dense class="mt-2">
                                                        <v-col cols="6">
                                                            <v-select
                                                                :value="input.templatePartId"
                                                                :items="availableTemplateParts"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Select Template Part"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                clearable
                                                                :loading="data.loadingTemplateParts"
                                                                :disabled="data.loadingTemplateParts"
                                                                @input="updateInputTemplatePart(input.id, $event)"
                                                            ></v-select>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-select
                                                                :value="input.sourceField || 'value'"
                                                                :items="data.templatePartSourceFields"
                                                                item-text="text"
                                                                item-value="value"
                                                                label="Source Field"
                                                                dense
                                                                outlined
                                                                hide-details
                                                                @input="updateInputTemplatePartSourceField(input.id, $event)"
                                                            ></v-select>
                                                        </v-col>
                                                    </v-row>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-tab-item>

                        <!-- JSON Response Format Tab -->
                        <v-tab-item>
                            <v-card flat>
                                <v-card-text>
                                    <div class="mb-4 pa-3 blue lighten-5 rounded">
                                        <div class="d-flex align-center">
                                            <v-icon left color="primary">mdi-information</v-icon>
                                            <strong>Configure JSON Response Format with Validation</strong>
                                        </div>
                                        <div class="text-body-2 mt-1">
                                            Define the expected JSON structure and validation rules will be automatically applied from the linked template part.
                                        </div>
                                    </div>

                                    <!-- Show status if JSON configuration exists -->
                                    <v-alert
                                        v-if="hasJsonConfiguration()"
                                        type="success"
                                        dense
                                        outlined
                                        class="mb-4"
                                    >
                                        <div class="d-flex align-center">
                                            <div>
                                                <strong>JSON Configuration Present</strong>
                                                <div class="text-body-2">
                                                    JSON Output Structure: <v-chip x-small color="success">Configured</v-chip>
                                                    <span v-if="hasJsonSchema()" class="ml-2">
                                                        JSON Schema: <v-chip x-small color="success">Generated</v-chip>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </v-alert>

                                    <v-btn
                                        color="primary"
                                        @click="openJsonSchemaDialog"
                                        outlined
                                    >
                                        <v-icon left>mdi-code-json</v-icon>
                                        {{ hasJsonConfiguration() ? 'Edit JSON Schema' : 'Configure JSON Schema' }}
                                    </v-btn>
                                </v-card-text>
                            </v-card>
                        </v-tab-item>
                    </v-tabs-items>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    @click="closeConfigureDialog"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="saveInputConfiguration"
                    :disabled="data.parsedInputs.length > 0 && data.parsedInputs.some(input => !input.source)"
                >
                    Save Configuration
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Save Configuration Warning Dialog -->
    <v-dialog
        v-model="data.showSaveWarningDialog"
        max-width="500"
        persistent
    >
        <v-card>
            <v-card-title class="headline">
                <v-icon left color="warning">mdi-alert</v-icon>
                Confirm Save Configuration
            </v-card-title>

            <v-card-text>
                <p>{{ data.saveWarningMessage }}</p>
                <p class="mt-3"><strong>Do you want to continue?</strong></p>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    @click="cancelSaveConfiguration"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="confirmSaveConfiguration"
                >
                    {{ data.configurePrompt?.editValue.specificity === 'System Default' && hasNonDynamicInputs() ? 'Create Template Copy' : data.configurePrompt?.editValue.specificity === 'System Default' ? 'Update System Prompt' : 'Update Prompt' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- JSON Schema Editor Dialog -->
    <v-dialog
        v-model="data.showJsonSchemaDialog"
        max-width="1400"
        persistent
    >
        <v-card>
            <v-card-title class="headline">
                <v-icon left color="primary">mdi-code-json</v-icon>
                Configure JSON Response Format
                <v-spacer></v-spacer>
                <v-btn icon @click="closeJsonSchemaDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <!-- Note: Validation removed - JSON output is used as a structure guide only -->

                <v-row>
                    <!-- JSON Output Editor -->
                    <v-col cols="6">
                        <v-card outlined>
                            <v-card-title class="subtitle-1 py-2 d-flex align-center" style="cursor: pointer;">
                                <v-icon small class="mr-2">{{ data.jsonOutputCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                                Expected JSON Output Structure
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="!data.jsonOutputCollapsed"
                                    color="primary"
                                    small
                                    @click.stop="generateJsonSchemaFromValidation"
                                    :loading="data.generatingSchema"
                                    :disabled="data.generatingSchema"
                                >
                                    <v-icon small left>mdi-auto-fix</v-icon>
                                    Generate Schema
                                </v-btn>
                            </v-card-title>
                            <v-divider></v-divider>
                            <v-card-text v-show="!data.jsonOutputCollapsed" style="max-height: 500px; overflow-y: auto;">
                                <v-textarea
                                    v-model="data.jsonOutputText"
                                    @input="updateJsonOutputText($event)"
                                    outlined
                                    dense
                                    rows="20"
                                    class="code-editor"
                                    placeholder="Enter your desired JSON output structure here..."
                                ></v-textarea>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- JSON Schema Editor -->
                    <v-col cols="6">
                        <v-card outlined>
                            <v-card-title class="subtitle-1 py-2 d-flex align-center" style="cursor: pointer;">
                                <v-icon small class="mr-2">{{ data.jsonSchemaCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                                JSON Schema (with Validation Rules)
                                <v-chip v-if="data.jsonSchema" x-small color="success" class="ml-2">Generated</v-chip>
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="data.jsonSchema && !data.jsonSchemaCollapsed"
                                    icon
                                    small
                                    @click.stop="data.editingJsonSchema = !data.editingJsonSchema"
                                    :color="data.editingJsonSchema ? 'primary' : 'default'"
                                    title="Toggle edit mode"
                                >
                                    <v-icon small>{{ data.editingJsonSchema ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                                </v-btn>
                            </v-card-title>
                            <v-divider></v-divider>
                            <v-card-text v-show="!data.jsonSchemaCollapsed" style="max-height: 500px; overflow-y: auto;">
                                <div v-if="!data.jsonSchema" class="text-center pa-8 grey--text">
                                    <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-code-json</v-icon>
                                    <div class="text-h6 mb-2">No Schema Generated Yet</div>
                                    <div class="text-body-2">
                                        Define your JSON output structure on the left, then click<br>
                                        <strong>"Generate Schema"</strong> to create the schema<br>
                                        with validation rules applied.
                                    </div>
                                </div>
                                <v-textarea
                                    v-else-if="data.editingJsonSchema"
                                    :value="JSON.stringify(data.jsonSchema, null, 2)"
                                    @input="updateJsonSchema($event)"
                                    outlined
                                    dense
                                    rows="20"
                                    class="code-editor"
                                ></v-textarea>
                                <div v-else class="json-schema-viewer">
                                    <div v-html="renderSchemaWithActions(data.jsonSchema, '')"></div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    @click="closeJsonSchemaDialog"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="saveJsonSchemaConfiguration"
                >
                    <v-icon left>mdi-content-save</v-icon>
                    Save JSON Configuration
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Rule Assignment Dialog -->
    <v-dialog
        v-model="data.showRuleAssignmentDialog"
        max-width="800"
        persistent
    >
        <v-card>
            <v-card-title class="headline">
                <v-icon left color="primary">mdi-playlist-check</v-icon>
                Apply Validation Rules
                <v-spacer></v-spacer>
                <v-btn icon @click="closeRuleAssignmentDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <div class="mb-3">
                    <div class="text-subtitle-2 mb-2">
                        <v-icon small left color="info">mdi-map-marker</v-icon>
                        Target Schema Path:
                    </div>
                    <v-chip color="primary" outlined class="mb-2">
                        <v-icon left small>{{ getTypeColor(data.selectedSchemaNode?.type) }}</v-icon>
                        {{ data.selectedSchemaNode?.type }}
                    </v-chip>
                    <div class="text-caption font-weight-medium">{{ data.selectedSchemaPath }}</div>
                </div>

                <v-divider class="my-3"></v-divider>

                <div class="text-subtitle-2 mb-3">
                    <v-icon small left>mdi-filter</v-icon>
                    Search and Select Rules:
                </div>

                <v-text-field
                    v-model="data.ruleSearchQuery"
                    label="Search validation rules..."
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    class="mb-3"
                ></v-text-field>

                <div class="mb-3">
                    <v-btn
                        small
                        outlined
                        color="primary"
                        @click="openCustomRuleDialog"
                        block
                    >
                        <v-icon left small>mdi-plus-circle</v-icon>
                        Add Custom Rules
                    </v-btn>

                    <!-- Show existing custom rules for this path -->
                    <v-alert
                        v-if="data.customRules[data.selectedSchemaPath] && Object.keys(data.customRules[data.selectedSchemaPath]).length > 0"
                        type="info"
                        dense
                        outlined
                        class="mt-2 text-caption"
                    >
                        <div class="font-weight-medium mb-1">Custom Rules Applied:</div>
                        <v-chip
                            v-for="(value, key) in data.customRules[data.selectedSchemaPath]"
                            :key="key"
                            x-small
                            color="info"
                            class="mr-1 mb-1"
                        >
                            {{ key }}: {{ Array.isArray(value) ? value.join(', ') : value }}
                        </v-chip>
                    </v-alert>
                </div>

                <!-- Template-based validation rules (only shown when template part is linked) -->
                <div v-if="data.availableWordChecks.length > 0" class="rules-list-container">
                    <v-divider class="my-3"></v-divider>

                    <div class="text-subtitle-2 mb-3">
                        <v-icon small left color="primary">mdi-file-document-check</v-icon>
                        Template Validation Rules:
                    </div>

                    <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-caption">
                            {{ getFilteredRulesCount() }} of {{ data.availableWordChecks.length }} rules
                        </span>
                        <v-btn
                            x-small
                            text
                            color="primary"
                            @click="toggleAllRulesForPath"
                        >
                            {{ areAllRulesSelected() ? 'Deselect All' : 'Select All' }}
                        </v-btn>
                    </div>

                    <v-expansion-panels flat accordion class="rules-selection-list">
                        <v-expansion-panel
                            v-for="[groupName, groupRules] in getGroupedRules()"
                            :key="groupName"
                        >
                            <v-expansion-panel-header class="rule-group-header">
                                <template v-slot:default>
                                    <div class="d-flex align-center justify-space-between" style="width: 100%;">
                                        <div class="d-flex align-center">
                                            <v-checkbox
                                                :input-value="areAllGroupRulesSelected(groupRules)"
                                                :indeterminate="areSomeGroupRulesSelected(groupRules)"
                                                @click.stop="toggleGroupRules(groupRules)"
                                                color="primary"
                                                hide-details
                                                class="mt-0 pt-0 mr-2"
                                                style="flex: 0 0 auto;"
                                            ></v-checkbox>
                                            <v-icon small class="mr-2">
                                                {{ groupName === 'Word Count' ? 'mdi-counter' :
                                                   groupName === 'Sentence Length' ? 'mdi-format-line-spacing' :
                                                   groupName === 'Excluded Words' ? 'mdi-cancel' :
                                                   groupName === 'Required Words' ? 'mdi-check-circle' :
                                                   groupName === 'Format' ? 'mdi-format-text' :
                                                   'mdi-cog' }}
                                            </v-icon>
                                            <span class="font-weight-medium">{{ groupName }}</span>
                                        </div>
                                        <v-chip x-small color="grey" outlined>
                                            {{ groupRules.length }}
                                        </v-chip>
                                    </div>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-list dense>
                                    <v-list-item
                                        v-for="rule in groupRules"
                                        :key="rule.value"
                                        @click="toggleRuleForPath(rule.value)"
                                        class="rule-list-item"
                                    >
                                        <v-list-item-action>
                                            <v-checkbox
                                                :input-value="isRuleSelectedForPath(rule.value)"
                                                @click.stop="toggleRuleForPath(rule.value)"
                                                color="primary"
                                                hide-details
                                            ></v-checkbox>
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title class="text-body-2">{{ rule.text }}</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <div v-if="getFilteredRules().length === 0" class="text-center py-5 text--secondary">
                        <v-icon large color="grey">mdi-filter-remove</v-icon>
                        <div class="text-body-2 mt-2">No rules match your search</div>
                    </div>

                    <div v-if="getSelectedRulesForPath().length > 0" class="mt-3">
                        <v-chip
                            v-for="ruleIndex in getSelectedRulesForPath().slice(0, 5)"
                            :key="ruleIndex"
                            small
                            close
                            color="primary"
                            class="mr-1 mb-1"
                            @click:close="toggleRuleForPath(ruleIndex)"
                        >
                            {{ data.availableWordChecks.find(wc => wc.value === ruleIndex)?.text }}
                        </v-chip>
                        <v-chip
                            v-if="getSelectedRulesForPath().length > 5"
                            small
                            color="grey"
                            class="mr-1 mb-1"
                        >
                            +{{ getSelectedRulesForPath().length - 5 }} more
                        </v-chip>
                    </div>
                </div>

                <!-- Message when no template part is linked -->
                <v-alert
                    v-if="data.availableWordChecks.length === 0"
                    type="info"
                    dense
                    outlined
                    class="mt-3"
                >
                    <v-icon left small>mdi-information</v-icon>
                    <strong>No Template Part Linked:</strong> Only custom AJV rules are available. Link a template part with validation rules to enable template-based validation.
                </v-alert>
            </v-card-text>

            <v-card-actions>
                <div class="text-caption text--secondary">
                    {{ getSelectedRulesForPath().length }} rule(s) selected
                </div>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    @click="closeRuleAssignmentDialog"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="saveRuleAssignment"
                >
                    <v-icon left>mdi-check</v-icon>
                    Apply Rules
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Custom AJV Rules Dialog -->
    <v-dialog
        v-model="data.showCustomRuleDialog"
        max-width="700"
        persistent
    >
        <v-card>
            <v-card-title class="text-h6 primary white--text">
                <v-icon left color="white">mdi-code-braces</v-icon>
                Add Custom Validation Rules
                <v-spacer></v-spacer>
                <v-btn
                    icon
                    color="white"
                    @click="data.showCustomRuleDialog = false"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pt-4">
                <div class="text-body-2 mb-4 grey--text text--darken-1">
                    Add standard JSON Schema validation rules to <strong>{{ data.selectedSchemaPath }}</strong>
                </div>

                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="data.customRuleForm.minLength"
                            label="Min Length"
                            type="number"
                            outlined
                            dense
                            hint="Minimum string length"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-arrow-expand-right</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="data.customRuleForm.maxLength"
                            label="Max Length"
                            type="number"
                            outlined
                            dense
                            hint="Maximum string length"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-arrow-collapse-left</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="data.customRuleForm.minItems"
                            label="Min Items"
                            type="number"
                            outlined
                            dense
                            hint="Minimum array items (for arrays)"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-arrow-expand-down</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="data.customRuleForm.maxItems"
                            label="Max Items"
                            type="number"
                            outlined
                            dense
                            hint="Maximum array items (for arrays)"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-arrow-collapse-up</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="data.customRuleForm.minimum"
                            label="Minimum Value"
                            type="number"
                            outlined
                            dense
                            hint="Minimum numeric value"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-greater-than-or-equal</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="data.customRuleForm.maximum"
                            label="Maximum Value"
                            type="number"
                            outlined
                            dense
                            hint="Maximum numeric value"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-less-than-or-equal</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12">
                        <v-text-field
                            v-model="data.customRuleForm.pattern"
                            label="Pattern (Regex)"
                            outlined
                            dense
                            hint="Regular expression pattern"
                            persistent-hint
                            clearable
                            placeholder="^[A-Za-z]+$"
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-regex</v-icon>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12">
                        <v-select
                            v-model="data.customRuleForm.format"
                            label="Format"
                            :items="[
                                { text: 'None', value: '' },
                                { text: 'Email', value: 'email' },
                                { text: 'URI', value: 'uri' },
                                { text: 'Date (YYYY-MM-DD)', value: 'date' },
                                { text: 'Date-Time', value: 'date-time' },
                                { text: 'IPv4 Address', value: 'ipv4' },
                                { text: 'IPv6 Address', value: 'ipv6' },
                                { text: 'UUID', value: 'uuid' },
                                { text: 'Hostname', value: 'hostname' }
                            ]"
                            outlined
                            dense
                            hint="Predefined format validation"
                            persistent-hint
                            clearable
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-format-text</v-icon>
                            </template>
                        </v-select>
                    </v-col>

                    <v-col cols="12">
                        <v-combobox
                            v-model="data.customRuleForm.enum"
                            label="Enum (Allowed Values)"
                            outlined
                            dense
                            multiple
                            small-chips
                            deletable-chips
                            hint="Press Enter to add each allowed value"
                            persistent-hint
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small>mdi-format-list-bulleted</v-icon>
                            </template>
                        </v-combobox>
                    </v-col>
                </v-row>

                <v-alert
                    type="info"
                    dense
                    outlined
                    class="mt-3 text-caption"
                >
                    <strong>Note:</strong> These rules follow the JSON Schema specification and are validated by AJV.
                    Leave fields empty to not apply that rule.
                    <ul class="mt-2 mb-0">
                        <li><strong>minLength/maxLength:</strong> For string validation</li>
                        <li><strong>minItems/maxItems:</strong> For array validation (overrides template row count rules)</li>
                        <li><strong>minimum/maximum:</strong> For number validation</li>
                        <li><strong>pattern:</strong> Regular expression for string matching</li>
                        <li><strong>format:</strong> Predefined string formats (email, URL, etc.)</li>
                        <li><strong>enum:</strong> List of allowed values</li>
                    </ul>
                </v-alert>
            </v-card-text>

            <v-card-actions>
                <v-btn
                    text
                    @click="clearCustomRulesForPath"
                    color="error"
                >
                    <v-icon left small>mdi-delete</v-icon>
                    Clear All
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    @click="data.showCustomRuleDialog = false"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="saveCustomRules"
                >
                    <v-icon left>mdi-check</v-icon>
                    Save Rules
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Prompt Type Management Dialog -->
    <v-dialog
        v-model="data.showPromptTypeDialog"
        max-width="900"
        persistent
    >
        <v-card>
            <v-card-title class="headline primary white--text">
                <v-icon left color="white">mdi-format-list-bulleted-type</v-icon>
                Manage Prompt Types
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="closePromptTypeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pt-4">
                <!-- Add/Edit Form -->
                <v-card outlined class="mb-4">
                    <v-card-title class="subtitle-1 py-2">
                        <v-icon small left>{{ data.editingPromptType ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                        {{ data.editingPromptType ? 'Edit Prompt Type' : 'Add New Prompt Type' }}
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="data.promptTypeForm.prompt_type"
                                    label="Prompt Type Code *"
                                    outlined
                                    dense
                                    hint="Unique identifier (e.g., 'content_generation')"
                                    persistent-hint
                                    :disabled="data.editingPromptType"
                                    placeholder="e.g., content_generation"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="data.promptTypeForm.description"
                                    label="Description *"
                                    outlined
                                    dense
                                    hint="Human-readable description"
                                    persistent-hint
                                    placeholder="e.g., Content Generation"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col class="d-flex justify-end">
                                <v-btn
                                    v-if="data.editingPromptType"
                                    text
                                    @click="resetPromptTypeForm"
                                    class="mr-2"
                                >
                                    Cancel Edit
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    @click="savePromptType"
                                    :loading="data.loadingPromptTypes"
                                    :disabled="!data.promptTypeForm.prompt_type || !data.promptTypeForm.description"
                                >
                                    <v-icon left small>{{ data.editingPromptType ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
                                    {{ data.editingPromptType ? 'Update Type' : 'Add Type' }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <!-- Existing Prompt Types List -->
                <v-card outlined>
                    <v-card-title class="subtitle-1 py-2">
                        <v-icon small left>mdi-view-list</v-icon>
                        Existing Prompt Types
                        <v-chip x-small class="ml-2">{{ data.promptTypes.length }}</v-chip>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                        <v-simple-table>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">Type Code</th>
                                        <th class="text-left">Description</th>
                                        <th class="text-center" width="150">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="type in data.promptTypes" :key="type.id || type.prompt_type">
                                        <td>
                                            <code class="pa-1 grey lighten-3 rounded">{{ type.prompt_type }}</code>
                                        </td>
                                        <td>{{ type.description }}</td>
                                        <td class="text-center">
                                            <v-btn
                                                icon
                                                small
                                                @click="editPromptType(type)"
                                                title="Edit"
                                            >
                                                <v-icon small color="primary">mdi-pencil</v-icon>
                                            </v-btn>
                                            <v-btn
                                                icon
                                                small
                                                @click="deletePromptType(type)"
                                                title="Delete"
                                                :disabled="data.loadingPromptTypes"
                                            >
                                                <v-icon small color="error">mdi-delete</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                    <tr v-if="data.promptTypes.length === 0">
                                        <td colspan="3" class="text-center pa-4 grey--text">
                                            No prompt types found. Add one above.
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>

                <v-alert
                    type="info"
                    dense
                    outlined
                    class="mt-4 text-caption"
                >
                    <strong>Note:</strong> Prompt types are used to categorize and organize AI prompts.
                    Be careful when deleting types that are currently in use by existing prompts.
                </v-alert>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    @click="closePromptTypeDialog"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Notification :notification="data.notification" />
  </div>
</template>

<style scoped>
.warning-border {
  border-left: 4px solid #ff9800 !important;
}

pre {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
}

.code-editor {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
}

.code-editor textarea {
  font-family: 'Courier New', Courier, monospace !important;
}

/* JSON Schema Viewer with Action Buttons */
.json-schema-viewer {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.6;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
  max-height: 500px;
  overflow-y: auto;
  white-space: pre;
}

.json-key {
  color: #0066cc;
  font-weight: 600;
}

.json-value {
  color: #008000;
}

.json-type {
  color: #666;
}

/* Collapse Toggle */
.collapse-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 4px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s;
  user-select: none;
  vertical-align: middle;
}

.collapse-toggle:hover {
  color: #2196F3;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 3px;
}

.collapse-toggle i {
  font-size: 16px;
  transition: transform 0.2s;
}

.collapsible-content {
  display: block;
}

.schema-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  margin-left: 8px;
  background: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  position: relative;
  vertical-align: middle;
}

.schema-action-btn:hover {
  background: #1976D2;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.schema-action-btn.has-rules {
  background: #4CAF50;
}

.schema-action-btn.has-rules:hover {
  background: #45a049;
}

.schema-action-btn .rules-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #f44336;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
  min-width: 18px;
  text-align: center;
}

/* Rules Selection List */
.rules-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.rules-selection-list {
  max-height: 350px;
  overflow-y: auto;
}

.rule-group-header {
  user-select: none;
}

.rule-group-header .v-expansion-panel-header__icon {
  margin-left: 8px;
}

.rule-list-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.rule-list-item:hover {
  background-color: #f5f5f5;
}

.version-table tbody tr:hover {
  background-color: #f8f9fa !important;
}

.version-active-row {
  background-color: #f1f8e9 !important;
}

.version-pre {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
}

.compare-changed-row {
  background-color: #fff8e1 !important;
}

.compare-table td {
  vertical-align: top !important;
}
</style>
